<template>
  <div class="camera-sync-page-wrapper">
    <section class="camera-sync-layout">
    <!-- 头部横幅与标题 -->
    <header class="sync-topbar">
      <div class="sync-topbar-title">
        <span class="pulse-dot"></span>
        摄像头人脸与年级库同步
      </div>
      <div style="display: flex; align-items: center; gap: 12px;">
        <nav class="sync-tabs">
          <button
            v-for="tab in tabList"
            :key="tab.key"
            type="button"
            class="sync-tab"
            :class="{ 'is-active': activeTab === tab.key }"
            @click="activeTab = tab.key"
          >
            <i :class="tab.icon"></i>
            <span>{{ tab.label }}</span>
          </button>
        </nav>
        <el-button 
          type="info" 
          plain 
          icon="el-icon-time" 
          size="small" 
          style="border-radius: 8px; font-weight: 600;"
          @click="openSyncLogDialog"
        >同步历史</el-button>
      </div>
    </header>

    <main class="sync-main">
      <div class="query-layout">
      <!-- 左侧学校树 -->
      <div class="dept-filter-pane">
        <div class="dept-filter-header">
          <i class="el-icon-school"></i>
          <span>选择机构 / 学校</span>
        </div>
        <DepartmentPane directory-scope="teaching" @tree-node-click="onTreeNodeClick" />
      </div>

      <!-- 右侧同步内容面板 -->
      <section class="query-main">
        <!-- 统计、说明、摄像头收成一行。最后修改：2026-10-14 -->
        <div v-if="schoolId" class="sync-head" v-loading="statsLoading">
          <div class="sync-head-row">
          <div class="sync-metrics">
            <div
              class="stat-card stat-blue"
              :class="{ 'stat-card-clickable': !!schoolId }"
              @click="openStatDetail(headStats.type, '')"
            >
              <span class="stat-label">{{ headStats.totalLabel }}</span>
              <span class="stat-value">{{ headStats.total }}<span class="unit">条</span></span>
            </div>
            <div
              class="stat-card stat-emerald"
              :class="{ 'stat-card-clickable': !!schoolId }"
              @click="openStatDetail(headStats.type, '1')"
            >
              <span class="stat-label">{{ headStats.okLabel }}</span>
              <span class="stat-value">{{ headStats.ok }}<span class="unit">条</span>
                <span class="stat-rate">{{ headStats.rate }}%</span>
              </span>
            </div>
            <div
              class="stat-card stat-orange"
              :class="{ 'stat-card-clickable': !!schoolId }"
              @click="openStatDetail(headStats.type, '2')"
            >
              <span class="stat-label">待同步/失败</span>
              <span class="stat-value">{{ headStats.fail }}<span class="unit">条</span></span>
            </div>
          </div>
          <div class="sync-head-tools">
            <el-button size="small" plain icon="el-icon-question" @click="guideVisible = true">说明</el-button>
            <el-select
              v-model="filterDeviceId"
              placeholder="请选择摄像头"
              size="small"
              class="device-select"
              @change="onFilterChange"
            >
              <el-option
                v-for="item in cameraList"
                :key="item.DeviceId"
                :label="item.DeviceName + (item.IsOnline === true ? ' (在线)' : (item.IsOnline === false ? ' (离线)' : ' (检测中...)'))"
                :value="item.DeviceId"
              ></el-option>
            </el-select>
            <el-button
              type="primary"
              plain
              icon="el-icon-refresh"
              size="small"
              title="刷新设备在线状态"
              @click="refreshCameraList"
            ></el-button>
            <el-button
              v-if="filterDeviceId"
              type="success"
              plain
              icon="el-icon-data-analysis"
              size="small"
              @click="openDeviceAuditDialog"
            >审计</el-button>
          </div>
          </div>
          <div v-if="headStats.tip" class="sync-head-tip">
            <i class="el-icon-info"></i>
            <span>{{ headStats.tip }}</span>
          </div>
        </div>

        <!-- 没有选择学校提示 -->
        <div v-if="!schoolId" class="notice-banner notice-info">
          <i class="el-icon-info"></i>
          <span>请先从左侧树形选择您要同步的年级或部门节点</span>
        </div>

        <!-- 双 Tab 数据明细展示 -->
        <div v-else class="detail-container">
          <!-- 筛选工具栏 -->
          <div class="filter-toolbar">
            <div class="filter-toolbar-inner">
              <div class="filter-toolbar-left">
                <el-radio-group
                  v-if="activeTab === 'person'"
                  v-model="personSubTab"
                  size="small"
                  class="toolbar-sub-tabs"
                  @change="onSubTabChange"
                >
                  <el-radio-button label="student">学生人脸</el-radio-button>
                  <el-radio-button label="teacher">教师人脸</el-radio-button>
                </el-radio-group>
                <el-select v-model="filterStatus" placeholder="全部同步状态" clearable size="small" class="filter-status-select" @change="onFilterChange">
                  <el-option label="已同步成功" value="1"></el-option>
                  <el-option label="待同步/已失败" value="2"></el-option>
                </el-select>
                <el-button
                  type="primary"
                  plain
                  icon="el-icon-refresh"
                  size="small"
                  title="刷新列表数据"
                  @click="handleRefresh"
                ></el-button>
                <el-input
                  v-if="activeTab === 'person'"
                  v-model="searchName"
                  placeholder="搜索人员姓名/学号..."
                  size="small"
                  clearable
                  prefix-icon="el-icon-search"
                  class="filter-search-input"
                  @keyup.enter.native="onFilterChange"
                  @clear="onFilterChange"
                ></el-input>
              </div>
              <div class="filter-toolbar-right">
                <el-button
                  v-if="activeTab === 'dept'"
                  type="primary"
                  icon="el-icon-refresh"
                  size="small"
                  :disabled="!isCurrentDeviceOnline"
                  :loading="syncLoading"
                  @click="handleBatchSyncGroups"
                >批量同步脸库</el-button>
                <template v-if="activeTab === 'person'">
                  <el-button
                    type="primary"
                    size="small"
                    icon="el-icon-check"
                    :disabled="selectedPeople.length === 0 || !isCurrentDeviceOnline"
                    :loading="syncLoading"
                    @click="handleSyncSelectedPeople"
                  >同步已选人员 ({{ selectedPeople.length }})</el-button>
                  <el-button
                    type="danger"
                    plain
                    size="small"
                    icon="el-icon-delete"
                    :disabled="selectedPeople.length === 0 || !isCurrentDeviceOnline"
                    :loading="syncLoading"
                    @click="handleDeleteSelectedPeople"
                  >批量删除人脸 ({{ selectedPeople.length }})</el-button>
                  <el-button
                    type="primary"
                    icon="el-icon-refresh"
                    size="small"
                    :loading="syncLoading"
                    @click="handleBatchSyncPersons"
                  >一键推送未同步人脸</el-button>
                </template>
              </div>
            </div>
          </div>

          <!-- TAB 1: 年级脸库列表 -->
          <div v-show="activeTab === 'dept'" class="detail-table-panel">
            <div class="detail-table-host">
            <el-table
              :data="groupDetails"
              v-loading="detailsLoading"
              class="modern-table"
              height="100%"
              style="width: 100%"
              empty-text="暂无映射数据"
            >
              <el-table-column prop="DepartmentName" label="脸库分类" min-width="180">
                <template slot-scope="scope">
                  <div class="table-cell-with-icon">
                    <i class="el-icon-collection-tag cell-prefix-icon text-indigo"></i>
                    <span class="cell-primary-text">{{ scope.row.DepartmentName }}</span>
                  </div>
                </template>
              </el-table-column>
              <el-table-column prop="DeviceName" label="下发摄像头" min-width="170" align="center" header-align="center">
                <template slot-scope="scope">
                  <div class="table-cell-with-icon" style="display: flex; align-items: center; justify-content: center; gap: 6px;">
                    <i class="el-icon-video-camera cell-prefix-icon" :style="{ color: isDeviceOnline(scope.row.DeviceId) ? '#10b981' : '#94a3b8' }"></i>
                    <span class="cell-primary-text">{{ scope.row.DeviceName }}</span>
                    <el-tooltip :content="isDeviceOnline(scope.row.DeviceId) ? '接收设备在线' : '接收设备离线，同步指令可能会延时或失败'" placement="top">
                      <span :class="['status-dot-mini', isDeviceOnline(scope.row.DeviceId) ? 'is-online' : 'is-offline']"></span>
                    </el-tooltip>
                  </div>
                </template>
              </el-table-column>
              <el-table-column prop="VendorGroupId" label="摄像头内置 ID" width="130">
                <template slot-scope="scope">
                  <el-tag v-if="scope.row.VendorGroupId > 0" size="small" type="success" effect="dark">
                    {{ scope.row.VendorGroupId }}
                  </el-tag>
                  <span v-else class="text-muted">待自动生成</span>
                </template>
              </el-table-column>
              <el-table-column prop="SyncStatus" label="同步状态" width="130">
                <template slot-scope="scope">
                  <el-tag :type="getStatusTagType(scope.row)" size="small">
                    <i v-if="showStatusLoading(scope.row)" class="el-icon-loading" style="margin-right: 4px;"></i>
                    {{ getStatusText(scope.row) }}
                  </el-tag>
                </template>
              </el-table-column>
              <el-table-column prop="ErrorMessage" label="错误描述" min-width="240" show-overflow-tooltip>
                <template slot-scope="scope">
                  <span class="text-danger">{{ scope.row.ErrorMessage || '—' }}</span>
                </template>
              </el-table-column>
              <el-table-column prop="LastSyncTime" label="更新时间" width="160"></el-table-column>
              <el-table-column label="操作" width="160" fixed="right" align="center">
                <template slot-scope="scope">
                  <el-button 
                    type="text" 
                    size="small" 
                    icon="el-icon-refresh"
                    :disabled="!isDeviceOnline(scope.row.DeviceId)"
                    @click="syncSingleGroup(scope.row)"
                  >同步</el-button>
                  <el-button 
                    type="text" 
                    size="small" 
                    icon="el-icon-delete"
                    class="delete-action"
                    :disabled="scope.row.SyncStatus !== 1 || scope.row.VendorGroupId === 0 || !isDeviceOnline(scope.row.DeviceId)"
                    @click="deleteSingleGroup(scope.row)"
                  >删除</el-button>
                </template>
              </el-table-column>
            </el-table>
            </div>

            <!-- 年级列表分页 -->
            <div class="pagination-wrap">
              <el-pagination
                layout="total, prev, pager, next, jumper"
                :current-page="groupPage"
                :page-size="pageSize"
                :total="groupTotal"
                @current-change="handleGroupPageChange"
              ></el-pagination>
            </div>
          </div>

          <!-- TAB 2: 人员人脸列表 -->
          <div v-show="activeTab === 'person'" class="detail-table-panel">
            <div class="detail-table-host">
            <el-table
              :data="personDetails"
              v-loading="detailsLoading"
              class="modern-table"
              height="100%"
              style="width: 100%"
              ref="personTable"
              empty-text="暂无映射数据"
              @selection-change="handleSelectionChange"
            >
              <el-table-column type="selection" width="55"></el-table-column>
              <el-table-column prop="PersonName" label="姓名" min-width="120">
                <template slot-scope="scope">
                  <span class="person-name">{{ scope.row.PersonName }}</span>
                </template>
              </el-table-column>
              <el-table-column label="头像" width="90" align="center">
                <template slot-scope="scope">
                  <oss-el-image
                    v-if="scope.row.PhotoUrl"
                    style="width: 40px; height: 40px; border-radius: 4px;"
                    :src="scope.row.PhotoUrl"
                    :preview-src-list="[scope.row.PhotoUrl]"
                    fit="cover"
                  >
                    <div slot="error" class="image-slot" style="display: flex; justify-content: center; align-items: center; width: 100%; height: 100%; background: #f5f7fa; color: #909399; font-size: 10px;">
                      图片
                    </div>
                  </oss-el-image>
                  <span v-else>—</span>
                </template>
              </el-table-column>
              <el-table-column prop="PersonType" label="类型" width="90">
                <template slot-scope="scope">
                  <el-tag size="mini" :type="scope.row.PersonType === 'student' ? 'indigo' : 'orange'">
                    {{ scope.row.PersonType === 'student' ? '学生' : '教师' }}
                  </el-tag>
                </template>
              </el-table-column>
              <el-table-column prop="DepartmentName" label="所属班级/部门" min-width="120"></el-table-column>
              <el-table-column prop="HasPhoto" label="照片状态" width="130">
                <template slot-scope="scope">
                  <el-tag :type="scope.row.HasPhoto ? 'success' : 'danger'" size="mini" effect="plain">
                    {{ scope.row.HasPhoto ? '已上传照片' : '无照片人脸' }}
                  </el-tag>
                </template>
              </el-table-column>
              <el-table-column prop="DeviceName" label="摄像头" min-width="160">
                <template slot-scope="scope">
                  <div class="table-cell-with-icon" style="display: flex; align-items: center; gap: 6px;">
                    <i class="el-icon-video-camera cell-prefix-icon" :style="{ color: isDeviceOnline(scope.row.DeviceId) ? '#10b981' : '#94a3b8' }"></i>
                    <span>{{ scope.row.DeviceName }}</span>
                    <el-tooltip :content="isDeviceOnline(scope.row.DeviceId) ? '接收设备在线' : '接收设备离线，同步指令可能会延时或失败'" placement="top">
                      <span :class="['status-dot-mini', isDeviceOnline(scope.row.DeviceId) ? 'is-online' : 'is-offline']"></span>
                    </el-tooltip>
                  </div>
                </template>
              </el-table-column>
              <el-table-column prop="SyncStatus" label="同步状态" width="140">
                <template slot-scope="scope">
                  <el-tag :type="getStatusTagType(scope.row)" size="mini">
                    <i v-if="showStatusLoading(scope.row)" class="el-icon-loading" style="margin-right: 4px;"></i>
                    {{ getStatusText(scope.row) }}
                  </el-tag>
                </template>
              </el-table-column>
              <el-table-column prop="ErrorMessage" label="异常原因" min-width="150" show-overflow-tooltip>
                <template slot-scope="scope">
                  <span class="text-danger">{{ scope.row.ErrorMessage || '—' }}</span>
                </template>
              </el-table-column>
              <el-table-column prop="LastSyncTime" label="更新时间" width="150"></el-table-column>
              <el-table-column label="操作" width="160" fixed="right" align="center">
                <template slot-scope="scope">
                  <el-button 
                    type="text" 
                    size="small" 
                    icon="el-icon-refresh"
                    :disabled="!scope.row.HasPhoto || !isDeviceOnline(scope.row.DeviceId)"
                    @click="syncSinglePerson(scope.row)"
                  >推送</el-button>
                  <el-button 
                    type="text" 
                    size="small" 
                    icon="el-icon-delete"
                    class="delete-action"
                    :disabled="scope.row.SyncStatus !== 1 || !isDeviceOnline(scope.row.DeviceId)"
                    @click="deleteSinglePerson(scope.row)"
                  >删除</el-button>
                </template>
              </el-table-column>
            </el-table>
            </div>

            <!-- 底部分页 -->
            <div class="pagination-wrap">
              <el-pagination
                layout="total, prev, pager, next, jumper"
                :current-page="personPage"
                :page-size="pageSize"
                :total="personTotal"
                @current-change="handlePersonPageChange"
              ></el-pagination>
            </div>
          </div>
        </div>
      </section>
      </div>
    </main>
    </section>

    <el-dialog
      :visible.sync="guideVisible"
      title="同步说明"
      width="560px"
      append-to-body
      custom-class="sync-guide-dialog"
    >
      <div class="guide-block">
        <span class="desc-badge badge-primary">推荐步骤</span>
        <ol class="guide-steps">
          <li>选择左侧学校</li>
          <li>在设备管理中录入摄像头</li>
          <li>同步年级脸库，并同步学校节点（教职工脸库）</li>
          <li>批量或单独同步学生 / 教师人脸</li>
        </ol>
      </div>
      <div class="guide-block">
        <span class="desc-badge badge-warning">清理说明</span>
        <p class="guide-text">支持删除分类或人脸。删除分类将自动清空设备上的人脸，并重置同步状态。</p>
      </div>
    </el-dialog>

    <!-- UI UX Pro Max 3.0 异步高保真同步确认弹窗 -->
    <el-dialog
      :visible.sync="syncDialog.visible"
      title="同步确认"
      width="560px"
      top="5vh"
      custom-class="modern-confirm-dialog"
      :append-to-body="true"
      :close-on-click-modal="false"
    >
      <div class="confirm-promax-container">
        <div class="promax-header">
          <div class="header-icon-box">
            <i class="el-icon-refresh-right rotating-icon"></i>
          </div>
          <div class="header-text">
            <h3 class="header-title">{{ syncDialog.type === 'group' ? '确认同步脸库分类？' : '确认推送未同步人脸？' }}</h3>
            <p class="header-subtitle">请确认同步的数据范围与目标接收设备</p>
          </div>
        </div>
        
        <!-- 区块一：同步数据源 -->
        <div class="info-section-wrapper">
          <div class="info-meta-row">
            <div class="meta-item">
              <span class="meta-label">所属学校</span>
              <span class="meta-value font-bold">{{ syncDialog.schoolName }}</span>
            </div>
            <div class="meta-item">
              <span class="meta-label">所选节点</span>
              <span class="meta-value node-badge">{{ syncDialog.scopeName }}</span>
            </div>
          </div>
          
          <div v-if="syncDialog.type === 'group'" class="grades-box">
            <div class="grades-label">即将同步的脸库:</div>
            <div class="grades-tags-container">
              <span 
                v-for="name in syncDialog.grades" 
                :key="name" 
                class="grade-confirm-tag"
              >{{ name }}</span>
            </div>
          </div>
          
          <div v-if="syncDialog.type === 'person'" class="grades-box">
            <div class="grades-label">即将推送人脸的人员:</div>
            <div class="grades-tags-container">
              <span 
                v-for="p in syncDialog.persons.slice(0, 8)" 
                :key="p.PersonId" 
                :class="['grade-confirm-tag', 'person-confirm-tag', { 'no-photo-tag': !p.HasPhoto }]"
                :title="p.HasPhoto ? '已上传照片' : '未上传照片，下发必然失败'"
              >
                {{ p.PersonName }}{{ p.HasPhoto ? '' : ' (无照片!)' }}
              </span>
              <span 
                v-if="syncDialog.persons.length > 8" 
                class="more-persons-link" 
                @click="personListDialog.visible = true"
              >
                等共 {{ syncDialog.persons.length }} 人 [点击查看明细]
              </span>
            </div>
          </div>
        </div>
        
        <!-- 分割指示器 -->
        <div class="flow-divider">
          <span class="divider-line"></span>
          <div class="divider-badge">
            <i class="el-icon-bottom"></i>
          </div>
          <span class="divider-line"></span>
        </div>
        
        <!-- 区块二：接收设备选择 -->
        <div class="device-section-wrapper">
          <div class="section-title-row">
            <span class="section-title">下发目标设备</span>
            <span class="section-tip">（点击卡片可多选或取消选择）</span>
          </div>
          <div class="modern-device-grid">
            <div 
              v-for="c in syncDialog.cameras"
              :key="c.DeviceId"
              :id="'confirm-cam-card-' + c.DeviceId" 
              :class="['modern-device-select-card', c.IsOnline ? 'device-online' : 'device-offline', c.isSelected ? 'is-selected' : '']"
              :title="c.IsOnline ? '设备在线，点击可取消勾选' : '设备已离线，无法选择下发'"
              @click="toggleCameraSelect(c)"
            >
              <div class="card-left-info">
                <i class="el-icon-video-camera card-camera-icon"></i>
                <div class="card-name-box">
                  <div class="card-device-title-row">
                    <span :class="['status-dot', c.IsOnline ? 'online' : 'offline']"></span>
                    <span class="card-device-name">{{ c.DeviceName }}</span>
                  </div>
                  <span class="card-device-sn">
                    SN: {{ c.DeviceId }}{{ c.IsOnline ? '' : ' (设备离线)' }}
                  </span>
                </div>
              </div>
              <div class="card-status-badge">
                <i class="el-icon-success status-check-icon"></i>
              </div>
            </div>
          </div>
        </div>
        
        <div class="warning-footer-banner">
          <i class="el-icon-warning"></i>
          <span>
            {{ syncDialog.type === 'group' 
                ? '提示：下发后，系统将对高亮选中的设备下发同步指令。' 
                : '提示：系统将把该节点下所有“待同步”或“已失败”的师生人脸推送至高亮选中的设备。' }}
          </span>
        </div>
      </div>
      
      <span slot="footer" class="dialog-footer">
        <el-button @click="syncDialog.visible = false" size="small">取消</el-button>
        <el-button 
          type="primary" 
          size="small" 
          :loading="syncLoading"
          @click="submitSyncConfirm"
        >
          {{ syncDialog.type === 'group' ? '确认开始' : '确认推送' }}
        </el-button>
      </span>
    </el-dialog>

    <!-- 同步历史日志 Dialog -->
    <el-dialog
      :visible.sync="logDialog.visible"
      title="同步操作历史"
      width="900px"
      top="5vh"
      custom-class="stat-detail-dialog"
      :append-to-body="true"
      :close-on-click-modal="false"
      @open="loadSyncLogPage"
    >
      <div style="padding: 10px 0 0 0;">

        <el-table :data="logDialog.list" v-loading="logDialog.loading" class="modern-table" max-height="390" style="width: 100%; margin-top: 10px;">
          <el-table-column prop="SyncType" label="操作类型" width="130">
            <template slot-scope="scope">
              <el-tag :type="getSyncTypeTag(scope.row.SyncType)" size="small">
                {{ getSyncTypeText(scope.row.SyncType) }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="Progress" label="执行进度" min-width="150">
            <template slot-scope="scope">
              <div style="display: flex; flex-direction: column; gap: 4px;">
                <span style="font-size: 11px; color: #64748b; font-weight: 600;">
                  已完成: {{ scope.row.SuccessCount + scope.row.FailCount }} / {{ scope.row.TotalCount }} (成功: <span class="text-success">{{ scope.row.SuccessCount }}</span>, 失败: <span class="text-danger">{{ scope.row.FailCount }}</span>)
                </span>
                <div class="bento-progress-wrapper" style="margin: 0; height: 3px; background: rgba(0,0,0,0.04);">
                  <div class="bento-progress-bar" :style="{ 
                    width: (scope.row.TotalCount > 0 ? ((scope.row.SuccessCount + scope.row.FailCount) / scope.row.TotalCount * 100) : 0) + '%',
                    background: scope.row.FailCount > 0 ? 'linear-gradient(90deg, #f59e0b, #ef4444)' : 'linear-gradient(90deg, #10b981, #34d399)'
                  }"></div>
                </div>
              </div>
            </template>
          </el-table-column>
          <el-table-column prop="Status" label="状态" width="110">
            <template slot-scope="scope">
              <el-tag :type="scope.row.Status === 1 ? 'success' : 'info'" size="small">
                {{ scope.row.Status === 1 ? '已完成' : '下发中/进行中' }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="TriggerUserName" label="操作人" width="120" show-overflow-tooltip></el-table-column>
          <el-table-column prop="StartTime" label="发起时间" width="160"></el-table-column>
          <el-table-column label="操作" width="100">
            <template slot-scope="scope">
              <el-button type="text" size="small" icon="el-icon-view" @click="viewLogDetail(scope.row)">明细</el-button>
            </template>
          </el-table-column>
        </el-table>

        <!-- 分页：无数据时也展示「共 0 条」 -->
        <div class="pagination-wrap" style="margin-top: 10px;">
          <el-pagination
            layout="total, prev, pager, next, jumper"
            :current-page="logDialog.page"
            :page-size="logDialog.pageSize"
            :total="logDialog.total"
            @current-change="handleLogPageChange"
          ></el-pagination>
        </div>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button @click="logDialog.visible = false" size="small">关闭</el-button>
      </span>
    </el-dialog>

    <!-- 任务明细 Dialog -->
    <el-dialog
      :visible.sync="detailDialog.visible"
      title="任务指令执行明细"
      width="850px"
      top="5vh"
      custom-class="stat-detail-dialog"
      :append-to-body="true"
      :close-on-click-modal="false"
      @open="loadDetailLogPage"
    >
      <div style="padding: 10px 0 0 0;">
        <!-- 任务信息说明 -->
        <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px;">
          <div style="font-size: 13px; color: #64748b;">
            批次 ID：<span style="color: #334155; font-weight: 600;">{{ detailDialog.logId }}</span>
            <span style="margin: 0 8px; color: #cbd5e1;">|</span>
            操作类型：<span style="color: #334155; font-weight: 600;">{{ getSyncTypeText(detailDialog.syncType) }}</span>
          </div>
        </div>

        <el-table :data="detailDialog.list" v-loading="detailDialog.loading" class="modern-table" style="width: 100%; margin-top: 10px;">
          <el-table-column prop="DeviceName" label="目标摄像头" min-width="130" show-overflow-tooltip></el-table-column>
          <el-table-column prop="TargetName" label="同步目标" min-width="145" show-overflow-tooltip>
            <template slot-scope="scope">
              <div>
                <div class="person-name">{{ scope.row.TargetName }}</div>
                <div style="font-size: 11px; color: #94a3b8; margin-top: 3px;">
                  ID：{{ scope.row.TargetId }}
                </div>
              </div>
            </template>
          </el-table-column>
          <el-table-column label="执行结果" width="180" align="center">
            <template slot-scope="scope">
              <div style="display: flex; flex-direction: column; align-items: center; gap: 4px; width: 100%;">
                <el-tag size="mini" type="info">{{ getActionText(scope.row.Action) }}</el-tag>
                <el-tag :type="getStatusTagType(scope.row)" size="mini">
                  {{ getStatusText(scope.row) }}
                </el-tag>
              </div>
            </template>
          </el-table-column>
          <el-table-column prop="ErrorMessage" label="反馈/异常描述" min-width="165" show-overflow-tooltip>
            <template slot-scope="scope">
              <span class="text-danger">{{ scope.row.ErrorMessage || '—' }}</span>
            </template>
          </el-table-column>
          <el-table-column prop="LastSyncTime" label="反馈时间" width="135"></el-table-column>
        </el-table>

        <!-- 分页 -->
        <div v-if="detailDialog.total > 0" class="pagination-wrap" style="margin-top: 10px;">
          <el-pagination
            layout="total, prev, pager, next"
            :current-page="detailDialog.page"
            :page-size="detailDialog.pageSize"
            :total="detailDialog.total"
            @current-change="handleDetailLogPageChange"
          ></el-pagination>
        </div>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button @click="detailDialog.visible = false" size="small">返回</el-button>
      </span>
    </el-dialog>

    <!-- 待下发人员明细弹窗 -->
    <el-dialog
      :visible.sync="personListDialog.visible"
      title="待下发人员明细"
      width="600px"
      top="5vh"
      custom-class="modern-confirm-dialog"
      :append-to-body="true"
      :close-on-click-modal="true"
    >
      <div class="confirm-promax-container" style="gap: 12px;">
        <div class="promax-header">
          <div class="header-icon-box" style="background: rgba(16, 185, 129, 0.1);">
            <i class="el-icon-user" style="font-size: 24px; color: #10b981;"></i>
          </div>
          <div class="header-text">
            <h3 class="header-title">待推送人脸人员明细</h3>
            <p class="header-subtitle">该节点下共有 {{ syncDialog.persons.length }} 名人员待同步或下发失败</p>
          </div>
        </div>

        <div style="margin-top: 10px;">
          <!-- 本地模糊查询过滤输入框 -->
          <el-input
            v-model="personListDialog.searchQuery"
            placeholder="输入姓名或班级部门搜索..."
            size="small"
            clearable
            prefix-icon="el-icon-search"
            style="margin-bottom: 12px;"
          ></el-input>

          <div class="grades-tags-container" style="max-height: 320px; display: flex; flex-wrap: wrap; gap: 8px; overflow-y: auto;">
            <span
              v-for="p in filteredDialogPersons"
              :key="p.PersonId"
              :class="['grade-confirm-tag', 'person-confirm-tag', { 'no-photo-tag': !p.HasPhoto }]"
              :title="p.HasPhoto ? '已上传照片' : '未上传照片，下发必然失败'"
            >
              {{ p.PersonName }} ({{ p.DepartmentName }}){{ p.HasPhoto ? '' : ' (无照片!)' }}
            </span>
            <div v-if="filteredDialogPersons.length === 0" style="width: 100%; text-align: center; color: #94a3b8; padding: 24px 0; font-size: 13px;">
              未找到匹配的待下发人员
            </div>
          </div>
        </div>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button @click="personListDialog.visible = false" size="small" type="primary">关闭</el-button>
      </span>
    </el-dialog>

    <!-- 统计指标数字点击明细弹窗 -->
    <el-dialog
      :visible.sync="statDetailDialog.visible"
      :title="statDetailDialog.title"
      width="900px"
      top="5vh"
      custom-class="stat-detail-dialog"
      :append-to-body="true"
      :close-on-click-modal="true"
    >
      <div style="padding: 10px 0 0 0;">
        <!-- 搜索与筛选回显同行 Flex 工具栏 -->
        <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 14px; gap: 12px;">
          <!-- 左侧搜索 -->
          <div style="display: flex; align-items: center; gap: 8px;">
            <el-input
              v-model="statDetailDialog.searchQuery"
              :placeholder="statDetailDialog.type === 'dept' ? '搜索脸库分类或摄像头名称...' : '搜索姓名/班级/学号...'"
              size="small"
              clearable
              prefix-icon="el-icon-search"
              style="width: 260px;"
              @keyup.enter.native="handleStatDetailSearch"
              @clear="handleStatDetailSearch"
            ></el-input>
            <el-button type="primary" size="small" icon="el-icon-search" @click="handleStatDetailSearch">查询</el-button>
          </div>
          <!-- 右侧筛选范围 -->
          <div style="font-size: 13px; color: #64748b;">
            学校：<span style="color: #334155; font-weight: 600;">{{ schoolName }}</span>
            <span style="margin: 0 8px; color: #cbd5e1;">|</span>
            设备：<span style="color: #334155; font-weight: 600;">{{ statDetailDialog.deviceName }}</span>
          </div>
        </div>

        <!-- 表格展示区 -->
        <div v-loading="statDetailDialog.loading">
          <!-- 年级列表 -->
          <el-table
            v-if="statDetailDialog.type === 'dept'"
            :data="statDetailDialog.dataList"
            class="modern-table"
            style="width: 100%"
            size="small"
            border
          >
            <el-table-column prop="DepartmentName" label="脸库分类" min-width="180">
              <template slot-scope="scope">
                <div class="table-cell-with-icon">
                  <i class="el-icon-collection-tag cell-prefix-icon text-indigo"></i>
                  <span class="cell-primary-text">{{ scope.row.DepartmentName }}</span>
                </div>
              </template>
            </el-table-column>
            <el-table-column label="下发设备" min-width="220">
              <template slot-scope="scope">
                <div class="table-cell-with-icon">
                  <i class="el-icon-video-camera cell-prefix-icon text-indigo"></i>
                  <div>
                    <div class="cell-primary-text">{{ scope.row.DeviceName }}</div>
                    <div style="font-size: 12px; color: #94a3b8; margin-top: 3px;">
                      内置 ID：{{ scope.row.VendorGroupId > 0 ? scope.row.VendorGroupId : '待自动生成' }}
                    </div>
                  </div>
                </div>
              </template>
            </el-table-column>
            <el-table-column label="同步结果" min-width="180" show-overflow-tooltip>
              <template slot-scope="scope">
                <div>
                  <el-tag :type="getStatusTagType(scope.row)" size="small">
                    {{ getStatusText(scope.row) }}
                  </el-tag>
                  <div
                    v-if="scope.row.ErrorMessage"
                    class="text-danger"
                    style="font-size: 12px; margin-top: 4px;"
                  >
                    {{ scope.row.ErrorMessage }}
                  </div>
                </div>
              </template>
            </el-table-column>
            <el-table-column prop="LastSyncTime" label="更新时间" width="160"></el-table-column>
          </el-table>

          <!-- 人员列表 -->
          <el-table
            v-else
            :data="statDetailDialog.dataList"
            class="modern-table"
            style="width: 100%"
            size="small"
            border
          >
            <el-table-column label="人员信息" min-width="230">
              <template slot-scope="scope">
                <div style="display: flex; align-items: center; gap: 8px;">
                  <oss-el-image
                    v-if="scope.row.PhotoUrl"
                    style="width: 40px; height: 40px; border-radius: 4px; flex: 0 0 auto;"
                    :src="scope.row.PhotoUrl"
                    :preview-src-list="[scope.row.PhotoUrl]"
                    fit="cover"
                  >
                    <div slot="error" class="image-slot" style="display: flex; justify-content: center; align-items: center; width: 100%; height: 100%; background: #f5f7fa; color: #909399; font-size: 10px;">
                      图片
                    </div>
                  </oss-el-image>
                  <span v-else style="width: 40px; text-align: center; color: #94a3b8;">—</span>
                  <div>
                    <div class="person-name">{{ scope.row.PersonName }}</div>
                    <div style="margin-top: 3px;">
                      <el-tag size="mini" :type="scope.row.PersonType === 'student' ? 'indigo' : 'orange'">
                        {{ scope.row.PersonType === 'student' ? '学生' : '教师' }}
                      </el-tag>
                      <el-tag
                        size="mini"
                        :type="scope.row.HasPhoto ? 'success' : 'danger'"
                        effect="plain"
                        style="margin-left: 4px;"
                      >
                        {{ scope.row.HasPhoto ? '有照片' : '无照片' }}
                      </el-tag>
                    </div>
                  </div>
                </div>
              </template>
            </el-table-column>
            <el-table-column prop="DepartmentName" label="所属班级/部门" min-width="120"></el-table-column>
            <el-table-column prop="DeviceName" label="摄像头" min-width="120"></el-table-column>
            <el-table-column label="同步结果" min-width="190" show-overflow-tooltip>
              <template slot-scope="scope">
                <div>
                  <el-tag :type="getStatusTagType(scope.row)" size="mini">
                    {{ getStatusText(scope.row) }}
                  </el-tag>
                  <div v-if="scope.row.ErrorMessage" class="text-danger" style="font-size: 12px; margin-top: 4px;">
                    {{ scope.row.ErrorMessage }}
                  </div>
                  <div v-else-if="!scope.row.HasPhoto" style="color: #94a3b8; font-size: 12px; margin-top: 4px;">
                    未上传照片，无法同步
                  </div>
                </div>
              </template>
            </el-table-column>
            <el-table-column prop="LastSyncTime" label="更新时间" width="150"></el-table-column>
          </el-table>

          <!-- 分页 -->
          <div v-if="statDetailDialog.total > 0" style="margin-top: 15px; text-align: right;">
            <el-pagination
              layout="total, prev, pager, next, jumper"
              :current-page="statDetailDialog.page"
              :page-size="statDetailDialog.pageSize"
              :total="statDetailDialog.total"
              @current-change="handleStatDetailPageChange"
            ></el-pagination>
          </div>
        </div>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button @click="statDetailDialog.visible = false" size="small" type="primary">关闭</el-button>
      </span>
    </el-dialog>

    <!-- 摄像头物理数据审计弹窗 -->
    <el-dialog
      :visible.sync="deviceAuditDialog.visible"
      title="摄像头物理人脸库审计"
      width="950px"
      top="5vh"
      custom-class="stat-detail-dialog"
      :append-to-body="true"
      :close-on-click-modal="true"
      @close="closeDeviceAuditDialog"
    >
      <div style="padding: 10px 0 0 0;">
        <!-- 头部信息提示及手动刷新 -->
        <div style="margin-bottom: 12px; font-size: 13px; color: #64748b; display: flex; justify-content: space-between; align-items: center;">
          <div>
            物理设备：<span style="color: #334155; font-weight: 600;">{{ getSelectedDeviceName() }}</span>
            <span style="margin: 0 8px; color: #cbd5e1;">|</span>
            网络状态：<span :class="isCurrentDeviceOnline ? 'text-success' : 'text-danger'" style="font-weight: 600;">
              {{ getCurrentDeviceNetworkLabel() }}
            </span>
          </div>
          <el-button type="text" icon="el-icon-refresh" size="small" @click="refreshCurrentAuditTab">刷新物理数据</el-button>
        </div>

        <div class="audit-tabs-container-wrapper" style="position: relative;">
          <!-- 精美高级物理审计加载蒙层 -->
          <div v-if="deviceAuditDialog.loading" class="audit-glass-loading-overlay">
            <div class="audit-loading-inner">
              <!-- 发光雷达光环 -->
              <div class="premium-loader-box">
                <div class="radar-glow-ring"></div>
                <div class="radar-scan-line"></div>
                <div class="cpu-core-icon">
                  <i class="el-icon-cpu"></i>
                </div>
              </div>

              <!-- 级联多步骤流动指引图 -->
              <div class="audit-cascade-steps">
                <div class="cascade-step" :class="getFlowStepClass(1)">
                  <div class="step-icon-circle">
                    <i class="el-icon-cloudy"></i>
                  </div>
                  <span class="step-label">云端系统</span>
                  <span class="step-status-txt">指令下发</span>
                </div>
                <div class="cascade-line" :class="getFlowLineClass(1)">
                  <span class="flow-pulse"></span>
                </div>

                <div class="cascade-step" :class="getFlowStepClass(2)">
                  <div class="step-icon-circle">
                    <i class="el-icon-connection"></i>
                  </div>
                  <span class="step-label">边缘网关</span>
                  <span class="step-status-txt">身份校验</span>
                </div>
                <div class="cascade-line" :class="getFlowLineClass(2)">
                  <span class="flow-pulse"></span>
                </div>

                <div class="cascade-step" :class="getFlowStepClass(3)">
                  <div class="step-icon-circle">
                    <i class="el-icon-video-camera animate-spin-custom"></i>
                  </div>
                  <span class="step-label">物理相机</span>
                  <span class="step-status-txt">读取芯片</span>
                </div>
                <div class="cascade-line" :class="getFlowLineClass(3)">
                  <span class="flow-pulse"></span>
                </div>

                <div class="cascade-step" :class="getFlowStepClass(4)">
                  <div class="step-icon-circle">
                    <i class="el-icon-data-analysis"></i>
                  </div>
                  <span class="step-label">前台渲染</span>
                  <span class="step-status-txt">脸库审计</span>
                </div>
              </div>

              <!-- 状态详细文本 -->
              <div class="audit-loading-desc-box">
                <div class="desc-spinner">
                  <i class="el-icon-loading"></i>
                </div>
                <div class="desc-text">{{ getDynamicLoadingText() }}</div>
              </div>
            </div>
          </div>

          <!-- 三个 Tab 内容区统一固定高度，列表分页贴底 -->
          <el-tabs
            v-model="deviceAuditDialog.activeTab"
            @tab-click="handleAuditTabClick"
            type="border-card"
            class="physical-audit-tabs"
          >
            <el-tab-pane name="stats" label="设备物理总数">
              <div class="physical-audit-pane physical-audit-stats-pane">
                <div style="font-size: 14px; color: #64748b; margin-bottom: 8px;">设备芯片端物理存储的注册人员总数</div>
                <div style="font-size: 48px; font-weight: 800; color: #10b981; font-family: 'Outfit', sans-serif;">
                  {{ deviceAuditDialog.stats.totalCount }}
                </div>
                <div style="font-size: 12px; color: #94a3b8; margin-top: 12px;">
                  * 该数字表示芯片实有人脸数，数据直接获取自设备端存储，不通过云端数据库。
                </div>
              </div>
            </el-tab-pane>

            <el-tab-pane name="group" label="物理分类列表 (Group)">
              <div class="physical-audit-pane physical-audit-list-pane">
                <el-table
                  :data="pagedAuditGroups"
                  class="modern-table"
                  height="320"
                  style="width: 100%;"
                  size="small"
                  border
                  empty-text="设备上暂无任何脸库分类记录"
                >
                  <el-table-column prop="groupId" label="摄像头内置分类 ID (groupId)" width="220" align="center">
                    <template slot-scope="scope">
                      <el-tag size="small" type="success" effect="dark">{{ scope.row.groupId }}</el-tag>
                    </template>
                  </el-table-column>
                  <el-table-column prop="groupName" label="分类名称 (groupName)" min-width="180">
                    <template slot-scope="scope">
                      <span style="font-weight: 600; color: #334155;">{{ scope.row.groupName }}</span>
                    </template>
                  </el-table-column>
                  <el-table-column prop="personCount" label="当前物理人脸数 (personCount)" width="220" align="center">
                    <template slot-scope="scope">
                      <span style="font-weight: bold; color: #059669;">{{ scope.row.personCount }}</span>
                    </template>
                  </el-table-column>
                </el-table>
                <div class="pagination-wrap">
                  <el-pagination
                    layout="total, prev, pager, next, jumper"
                    :current-page="deviceAuditDialog.groupPage"
                    :page-size="deviceAuditDialog.groupPageSize"
                    :total="deviceAuditDialog.groups.length"
                    @current-change="handleAuditGroupPageChange"
                  ></el-pagination>
                </div>
              </div>
            </el-tab-pane>

            <el-tab-pane name="person" label="物理注册人员 (Persons)">
              <div class="physical-audit-pane physical-audit-list-pane">
                <el-table
                  :data="deviceAuditDialog.persons"
                  class="modern-table"
                  height="320"
                  style="width: 100%;"
                  size="small"
                  border
                  empty-text="设备上暂无任何注册人员人脸记录"
                >
                  <el-table-column prop="personId" label="人员 ID (personId)" min-width="220" show-overflow-tooltip></el-table-column>
                  <el-table-column prop="name" label="姓名" min-width="140">
                    <template slot-scope="scope">
                      <span style="font-weight: 600; color: #334155;">{{ scope.row.name || '—' }}</span>
                    </template>
                  </el-table-column>
                  <el-table-column prop="serialNumber" label="编号" min-width="180" align="center">
                    <template slot-scope="scope">
                      <span>{{ scope.row.serialNumber || '—' }}</span>
                    </template>
                  </el-table-column>
                </el-table>
                <div class="pagination-wrap">
                  <el-pagination
                    :key="deviceAuditDialog.totalPersons + '_' + deviceAuditDialog.persons.length"
                    layout="total, prev, pager, next, jumper"
                    :current-page="deviceAuditDialog.pageIndex + 1"
                    :page-size="deviceAuditDialog.pageSize"
                    :total="deviceAuditDialog.totalPersons || deviceAuditDialog.persons.length"
                    @current-change="handleAuditPageChange"
                  ></el-pagination>
                </div>
              </div>
            </el-tab-pane>
          </el-tabs>
        </div>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button @click="deviceAuditDialog.visible = false" size="small" type="primary">关闭审计</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { getDepartmentType } from '../../api/modules/department';
import { getCameraPersonSyncStats, getCameraGroupDetailPage, getCameraPersonDetailPage, syncCameraGroups, syncCameraPersons, deleteCameraGroup, deleteCameraPersons, getKqtCameraSyncLogPage, getKqtCameraSyncLogDetailPage, queryDevicePhysical, probeDevicePhysical, getQueryStatus } from '../../api/modules/integration';
import { getKqtCameraListBySchool, getKqtCameraOnlineDeviceIds } from '../../api/modules/attendance';
import { BaseApiUrl } from '../../api/base';
import DepartmentPane from "../../components/DepartmentPane";
import { treeNodeDeptId } from '../../utils/deptCascaderProps';

export default {
  components: { DepartmentPane },
  data() {
    return {
      BaseApiUrl: BaseApiUrl,
      schoolId: "",
      schoolName: "",
      departmentId: 0,
      departmentName: "",
      selectedGrades: [],
      guideVisible: false,
      activeTab: "dept",
      personSubTab: "student",
      tabList: [
        { key: "dept", label: "脸库同步", icon: "el-icon-folder-opened" },
        { key: "person", label: "人员人脸同步", icon: "el-icon-user" }
      ],
      
      // 筛选条件
      filterDeviceId: "",
      filterStatus: "",
      searchName: "",
      
      // 列表数据
      cameraList: [],
      groupDetails: [],
      personDetails: [],
      selectedPeople: [],
      
      // 状态统计
      groupStats: { TotalCount: 0, SyncedCount: 0, UnsyncedCount: 0, Tip: "" },
      personStats: { TotalCount: 0, SyncedCount: 0, UnsyncedCount: 0, Tip: "" },
      
      // Loading & 分页
      statsLoading: false,
      detailsLoading: false,
      syncLoading: false,
      
      pageSize: 10,
      groupPage: 1,
      groupTotal: 0,
      personPage: 1,
      personTotal: 0,

      // 同步确认弹窗数据
      syncDialog: {
        visible: false,
        title: "同步确认",
        type: "group",
        scopeName: "",
        schoolName: "",
        grades: [],
        cameras: [],
        persons: [] // 待同步或推送失败的人员人脸列表
      },
      // 待同步人员明细弹窗数据
      personListDialog: {
        visible: false, // 是否显示明细弹窗
        searchQuery: "" // 模糊检索关键字
      },
      // 统计数字点击明细弹窗数据
      statDetailDialog: {
        visible: false,
        title: "",
        loading: false,
        type: "",      // 'dept' 或 'person'
        status: "",    // '1' 成功, '2' 失败, '' 全部
        page: 1,
        pageSize: 10,
        total: 0,
        searchQuery: "",
        deviceName: "", // 当前查询筛选的摄像头设备名
        dataList: []
      },
      // 同步历史日志弹窗数据
      logDialog: {
        visible: false,
        loading: false,
        list: [],
        page: 1,
        pageSize: 8,
        total: 0
      },
      // 物理数据审计弹窗数据
      deviceAuditDialog: {
        visible: false,
        activeTab: "stats",
        loading: false,
        taskId: "",
        timer: null,
        stats: {
          totalCount: 0
        },
        groups: [],
        persons: [],
        totalPersons: 0,
        pageIndex: 0,
        // 摄像头 queryPersons 协议分页大小。
        pageSize: 30,
        groupPage: 1,
        groupPageSize: 10,
        // 优化物理审计加载状态及步骤追踪计数器
        loadingSeconds: 0,
        currentStep: 1 // 1: 云端下发, 2: 边缘网关中转, 3: 物理相机芯片读取, 4: 前台解析渲染
      },
      // 日志明细弹窗数据
      detailDialog: {
        visible: false,
        loading: false,
        logId: 0,
        syncType: 0,
        list: [],
        page: 1,
        pageSize: 10,
        total: 0
      },
      // 主列表进行中状态的自动刷新定时器
      listPollTimer: null,
      mqProbeStarted: {},
      // MQ 在线状态缓存，避免每次重新加载设备列表都重复下发查询指令。
      mqOnlineCache: {},
      mqOnlineCacheTtl: 20 * 60 * 1000
    };
  },
  computed: {
    // 判断当前所选摄像头是否可执行物理查询；MQ 设备通过查询响应判断，不复用工控机在线状态。
    isCurrentDeviceOnline() {
      if (!this.filterDeviceId || !this.cameraList) return false;
      const cam = this.cameraList.find(c => c.DeviceId === this.filterDeviceId);
      return cam ? cam.IsOnline === true : false;
    },
    groupSuccessRate() {
      const total = this.groupStats.TotalCount || 0;
      const synced = this.groupStats.SyncedCount || 0;
      if (total === 0) return 0;
      return Math.min(100, Math.round((synced / total) * 100));
    },
    personSuccessRate() {
      const total = this.personStats.TotalCount || 0;
      const synced = this.personStats.SyncedCount || 0;
      if (total === 0) return 0;
      return Math.min(100, Math.round((synced / total) * 100));
    },
    /** 顶栏统计随当前页签切换。最后修改：2026-10-14 */
    headStats() {
      const isDept = this.activeTab === 'dept';
      const stats = isDept ? this.groupStats : this.personStats;
      return {
        type: isDept ? 'dept' : 'person',
        totalLabel: isDept ? '预计总映射' : '预计总人次',
        okLabel: isDept ? '同步成功' : '已写入成功',
        total: stats.TotalCount || 0,
        ok: stats.SyncedCount || 0,
        fail: stats.UnsyncedCount || 0,
        rate: isDept ? this.groupSuccessRate : this.personSuccessRate,
        tip: stats.Tip || ''
      };
    },
    // 对待同步人员进行本地模糊搜索过滤的计算属性
    filteredDialogPersons() {
      const query = (this.personListDialog.searchQuery || "").trim().toLowerCase();
      if (!query) {
        return this.syncDialog.persons || [];
      }
      return (this.syncDialog.persons || []).filter(p => 
        (p.PersonName && p.PersonName.toLowerCase().includes(query)) ||
        (p.DepartmentName && p.DepartmentName.toLowerCase().includes(query))
      );
    },
    auditTotalPersons() {
      const total = this.deviceAuditDialog.totalPersons || 0;
      if (total > 0) return total;
      return this.deviceAuditDialog.stats.totalCount || this.deviceAuditDialog.persons.length || 0;
    },
    auditCurrentPage() {
      const idx = this.deviceAuditDialog.pageIndex;
      if (typeof idx !== 'number' || isNaN(idx)) return 1;
      return idx + 1;
    },
    pagedAuditGroups() {
      const start = (this.deviceAuditDialog.groupPage - 1)
        * this.deviceAuditDialog.groupPageSize;
      return this.deviceAuditDialog.groups.slice(
        start,
        start + this.deviceAuditDialog.groupPageSize
      );
    }
  },
  methods: {
    // 打开设备物理数据审计弹窗
    openDeviceAuditDialog() {
      if (!this.filterDeviceId) {
        this.$message.warning("请先选择特定摄像头以进行物理审计。");
        return;
      }
      if (!this.isCurrentDeviceOnline) {
        this.$message.warning("当前选中的摄像头处于离线状态，无法进行物理数据审计。请检查摄像头连接或网关状态。");
        return;
      }
      this.deviceAuditDialog.visible = true;
      this.deviceAuditDialog.activeTab = "stats";
      this.deviceAuditDialog.pageIndex = 0;
      this.deviceAuditDialog.groupPage = 1;
      this.deviceAuditDialog.totalPersons = 0;
      this.deviceAuditDialog.persons = [];
      this.deviceAuditDialog.groups = [];
      this.deviceAuditDialog.stats.totalCount = 0;
      
      this.loadAuditData("stats");
    },

    // 关闭设备物理审计弹窗并清理轮询
    closeDeviceAuditDialog() {
      if (this.deviceAuditDialog.timer) {
        clearInterval(this.deviceAuditDialog.timer);
        this.deviceAuditDialog.timer = null;
      }
      this.deviceAuditDialog.loading = false;
      this.deviceAuditDialog.visible = false;
    },

    // 处理 Tab 点击切换
    handleAuditTabClick(tab) {
      if (this.deviceAuditDialog.timer) {
        clearInterval(this.deviceAuditDialog.timer);
        this.deviceAuditDialog.timer = null;
      }
      this.deviceAuditDialog.loading = false;
      
      // 切换 Tab 时重置分页（若切换至人员 Tab）
      if (tab.name === "person") {
        this.deviceAuditDialog.pageIndex = 0;
      } else if (tab.name === "group") {
        this.deviceAuditDialog.groupPage = 1;
      }
      this.loadAuditData(tab.name);
    },

    // 刷新当前物理数据
    refreshCurrentAuditTab() {
      this.loadAuditData(this.deviceAuditDialog.activeTab);
    },

    // 发起物理数据查询并短轮询
    loadAuditData(type) {
      if (this.deviceAuditDialog.timer) {
        clearInterval(this.deviceAuditDialog.timer);
        this.deviceAuditDialog.timer = null;
      }

      this.deviceAuditDialog.loading = true;
      this.deviceAuditDialog.loadingSeconds = 0;
      this.deviceAuditDialog.currentStep = 1;
      
      // 映射 action
      let action = "query_person_stats";
      if (type === "group") {
        action = "query_group";
      } else if (type === "person") {
        action = "query_persons";
      }

      // 请求入参
      const params = {
        schoolId: this.schoolId,
        deviceId: this.filterDeviceId,
        action: action
      };

      if (type === "person") {
        params.pageIndex = this.deviceAuditDialog.pageIndex;
        params.pageSize = this.deviceAuditDialog.pageSize;
      }

      // 调用接口下发指令
      queryDevicePhysical(params)
        .then(res => {
          if (res.data && res.data.success) {
            const taskId = res.data.response;
            this.deviceAuditDialog.taskId = taskId;
            
            // 启动短轮询，记录等待时间并流转状态步骤
            let pollCount = 0;
            this.deviceAuditDialog.timer = setInterval(() => {
              pollCount++;
              this.deviceAuditDialog.loadingSeconds = pollCount;
              
              // 步骤流转模拟：1~2秒指令下发与安全通道建立，3~4秒边缘中转身份校验，5秒起进入物理芯片闪存读取阶段
              if (pollCount <= 2) {
                this.deviceAuditDialog.currentStep = 1;
              } else if (pollCount <= 4) {
                this.deviceAuditDialog.currentStep = 2;
              } else {
                this.deviceAuditDialog.currentStep = 3;
              }

              if (pollCount > 25) { // 考虑到物理提取人脸数据大包较慢，最长放宽到 25 秒
                clearInterval(this.deviceAuditDialog.timer);
                this.deviceAuditDialog.timer = null;
                this.deviceAuditDialog.loading = false;
                this.$message.error("设备物理数据查询超时，请确认摄像头局域网是否在线且边缘服务是否正常。");
                return;
              }
              this.pollAuditStatus(taskId, type);
            }, 1000);
          } else {
            this.deviceAuditDialog.loading = false;
            this.$message.error(res.data ? res.data.msg : "下发物理数据查询指令失败");
          }
        })
        .catch(err => {
          this.deviceAuditDialog.loading = false;
          this.$message.error("下发物理审计指令请求异常: " + (err.message || err));
        });
    },

    // 轮询查询结果状态
    pollAuditStatus(taskId, type) {
      getQueryStatus({ taskId })
        .then(res => {
          if (res.data && res.data.success) {
            // 如果仍在处理中则继续等待
            if (res.data.msg === "processing") {
              return;
            }
            
            // 已完成，清理定时器并进入步骤 4 (前台解析渲染)
            clearInterval(this.deviceAuditDialog.timer);
            this.deviceAuditDialog.timer = null;
            this.deviceAuditDialog.currentStep = 4;

            // 延迟一点点关掉 Loading，让步骤 4 动画被用户看到 600ms，显得非常平滑和具有仪式感
            setTimeout(() => {
              this.deviceAuditDialog.loading = false;

              const responseData = res.data.response;
              if (responseData && responseData.status == 0) {
                const innerData = responseData.data;

                // 定义递归搜寻 totalCount 属性的闭包函数，解决层级和大小写敏感问题
                const findTotalCount = (obj) => {
                  if (!obj || typeof obj !== 'object') return 0;
                  for (let key in obj) {
                    if (key.toLowerCase() === 'totalcount') {
                      const val = parseInt(obj[key], 10);
                      if (!isNaN(val)) return val;
                    }
                    if (typeof obj[key] === 'object') {
                      const val = findTotalCount(obj[key]);
                      if (val > 0) return val;
                    }
                  }
                  return 0;
                };

                if (type === "stats") {
                  const tCount = findTotalCount(responseData);
                  this.deviceAuditDialog.stats.totalCount = tCount;
                } else if (type === "group") {
                  let groupList = [];
                  if (Array.isArray(innerData)) {
                    groupList = innerData;
                  } else if (innerData && typeof innerData === "object") {
                    groupList = innerData.groupList
                      || innerData.groups
                      || innerData.data
                      || (innerData.groupId !== undefined ? [innerData] : []);
                  }
                  this.deviceAuditDialog.groups = Array.isArray(groupList) ? groupList : [];
                } else if (type === "person") {
                  // 兼容多层嵌套结构，当包含 totalCount 时返回的是整个 root 节点，数据多了一层 data 包裹
                  let pList = [];
                  if (innerData) {
                    if (innerData.personList) {
                      pList = innerData.personList;
                    } else if (innerData.data && innerData.data.personList) {
                      pList = innerData.data.personList;
                    }
                  }
                  this.deviceAuditDialog.persons = Array.isArray(pList) ? pList : [];
                  
                  // 从整个响应包深度获取 totalCount 的数字值
                  const tCount = findTotalCount(responseData);
                  // 若物理明细中缺省了总数，则采用先前从“设备物理总数”中拉取到的总数，或者当前页人员数作为兜底，以正确唤醒分页组件
                  const finalTotal = tCount > 0 ? tCount : (this.deviceAuditDialog.stats.totalCount || this.deviceAuditDialog.persons.length || 0);
                  this.deviceAuditDialog.totalPersons = finalTotal;
                }
              } else {
                const errMsg = responseData ? responseData.detail : "物理设备返回异常数据";
                this.$message.error("审计数据获取失败: " + errMsg);
              }
            }, 600);
          } else {
            clearInterval(this.deviceAuditDialog.timer);
            this.deviceAuditDialog.timer = null;
            this.deviceAuditDialog.loading = false;
            this.$message.error(res.data ? res.data.msg : "轮询审计状态失败");
          }
        })
        .catch(err => {
          clearInterval(this.deviceAuditDialog.timer);
          this.deviceAuditDialog.timer = null;
          this.deviceAuditDialog.loading = false;
          this.$message.error("轮询审计状态请求异常: " + (err.message || err));
        });
    },

    // 获取审计加载步骤状态样式
    getFlowStepClass(step) {
      if (this.deviceAuditDialog.currentStep > step) return 'is-success';
      if (this.deviceAuditDialog.currentStep === step) return 'is-active';
      return 'is-wait';
    },

    // 获取步骤间连接线样式
    getFlowLineClass(step) {
      if (this.deviceAuditDialog.currentStep > step) return 'is-success';
      if (this.deviceAuditDialog.currentStep === step) return 'is-active';
      return 'is-wait';
    },

    // 根据秒数和步骤获取动态提示文案
    getDynamicLoadingText() {
      const seconds = this.deviceAuditDialog.loadingSeconds;
      const step = this.deviceAuditDialog.currentStep;
      
      if (step === 1) {
        return `[云端系统] 正在发起加密审计指令，目标设备: ${this.getSelectedDeviceName()}... (${seconds}秒)`;
      } else if (step === 2) {
        return `[边缘网关] 已接收审计请求，正在调度局域网加密通道并校验相机安全证书... (${seconds}秒)`;
      } else if (step === 3) {
        if (seconds < 8) {
          return `[物理相机] 通道握手成功，已连通摄像头物理工控板，正在从Flash闪存检索人员数据... (${seconds}秒)`;
        } else if (seconds < 14) {
          return `[物理相机] 相机主板低频芯片正在序列化大脸库JSON（含特征指针），请耐心等待... (${seconds}秒)`;
        } else {
          return `[物理相机] 脸库数据块准备就绪，正在通过局域网流式分片回传至边缘网关... (${seconds}秒)`;
        }
      } else if (step === 4) {
        return `[前台系统] 审计数据包拉取完成，正在解析包体并在前端安全沙箱中渲染账目明细... (${seconds}秒)`;
      }
      return `正在获取物理摄像头芯片级数据，此操作涉及局域网边缘穿透，请稍候... (${seconds}秒)`;
    },

    // 物理人员分页页码改变
    handleAuditPageChange(page) {
      // 插件是 1 索引，而设备 API 接口是 0 索引
      this.deviceAuditDialog.pageIndex = page - 1;
      this.loadAuditData("person");
    },

    // 切换物理分类分页，分类数据已一次性从设备获取。
    handleAuditGroupPageChange(page) {
      this.deviceAuditDialog.groupPage = page;
    },

    // 获取审计人员类型标签样式
    getAuditPersonTypeTag(type) {
      if (type === "staff") return "info";
      if (type === "whitelist") return "success";
      if (type === "blocklist") return "danger";
      if (type === "visitor") return "warning";
      return "primary";
    },

    // 获取审计人员类型文本
    getAuditPersonTypeText(type) {
      if (type === "staff") return "普通人员";
      if (type === "whitelist") return "白名单";
      if (type === "blocklist") return "黑名单";
      if (type === "visitor") return "访客";
      return type || "未知";
    },

    // 格式化物理设备返回的 updateTime 字符串 (YYYYMMDDhhmmss)
    formatAuditTime(timeStr) {
      if (!timeStr || timeStr.length < 14) return timeStr || "—";
      return `${timeStr.substr(0, 4)}-${timeStr.substr(4, 2)}-${timeStr.substr(6, 2)} ${timeStr.substr(8, 2)}:${timeStr.substr(10, 2)}:${timeStr.substr(12, 2)}`;
    },

    // 获取当前选中的摄像头名称
    getSelectedDeviceName() {
      if (!this.filterDeviceId) return "";
      const cam = this.cameraList.find(c => c.DeviceId === this.filterDeviceId);
      return cam ? cam.DeviceName : this.filterDeviceId;
    },

    // 根据摄像头连接模式显示准确的网络状态说明。
    getCurrentDeviceNetworkLabel() {
      if (!this.filterDeviceId || !this.cameraList) return "未知";
      const cam = this.cameraList.find(c => c.DeviceId === this.filterDeviceId);
      if (!cam || cam.IsOnline !== true) return "离线";
      return cam.ConnMode === "Mq"
        ? "在线（平台直连 / MQTT）"
        : "在线（局域网工控机）";
    },

    // 递归获取节点下所有子年级节点的名称
    getGradesFromNode(node) {
      const grades = [];
      const traverse = (n) => {
        if (!n) return;
        const label = n.data ? (n.data.label || n.data.name || "") : "";
        if (label && label.endsWith("级")) {
          grades.push(label);
        } else if (n.childNodes && n.childNodes.length > 0) {
          n.childNodes.forEach(child => traverse(child));
        }
      };
      traverse(node);
      return grades;
    },

    onTreeNodeClick(data, node) {
      if (!data || !data.value) {
        this.schoolId = "";
        this.departmentId = 0;
        this.departmentName = "";
        this.selectedGrades = [];
        return;
      }
      
      const nodeId = treeNodeDeptId(data);
      this.departmentId = nodeId || 0;
      this.departmentName = data.label || data.name || "";
      this.filterStatus = "";
      this.searchName = "";
      this.groupPage = 1;
      this.personPage = 1;

      // 递归获取当前选中节点下的所有子年级列表
      if (node) {
        this.selectedGrades = this.getGradesFromNode(node);
      } else {
        this.selectedGrades = [];
      }
      
      // 获取当前部门归属的学校
      getDepartmentType({ id: nodeId }).then((res) => {
        if (res.data.success && res.data.response) {
          const nextSchoolId = res.data.response.Id.toString();
          const schoolChanged = this.schoolId !== nextSchoolId;
          this.schoolId = nextSchoolId;
          this.schoolName = res.data.response.Name || res.data.response.ShortName || "当前学校";
          const loadData = () => {
            this.loadStats();
            this.loadDetails();
          };
          if (schoolChanged) {
            this.filterDeviceId = "";
            this.mqProbeStarted = {};
            this.loadCameraList().then(loadData);
          } else {
            // 同一学校内切换年级时，只刷新业务数据，不重复检测摄像头在线状态。
            loadData();
          }
        } else {
          this.schoolId = "";
          this.$message.error("获取所属学校失败");
        }
      }).catch(() => {
        this.schoolId = "";
        this.$message.error("获取学校信息网络异常");
      });
    },
    
    // 加载当前学校的摄像头下拉框
    loadCameraList() {
      // 同步页只展示校门/宿舍人脸考勤设备。最后更新：2026-10-26
      return getKqtCameraListBySchool({ schoolId: this.schoolId, usage: "PersonSync" })
        .then((res) => {
          if (res.data && res.data.success) {
            let list = res.data.response || [];
            // 前端再挡一层，避免旧接口/缓存把「暂不参与」「仅联动录像」带进来
            list = list.filter((c) =>
              c && c.IsAttendanceEnabled === true
              && (c.AttendanceScene === "Gate" || c.AttendanceScene === "Dorm")
            );
            list.forEach(item => {
              const cacheKey = `${this.schoolId}:${item.DeviceId}`;
              const cached = this.mqOnlineCache[cacheKey];
              const cacheValid = cached
                && Date.now() - cached.timestamp < this.mqOnlineCacheTtl;
              this.$set(item, 'IsOnline', cacheValid ? cached.isOnline : undefined);
            });
            this.cameraList = list;
            const selectedDeviceExists = list.some(
              item => item.DeviceId === this.filterDeviceId
            );
            if (list.length > 0 && !selectedDeviceExists) {
              this.filterDeviceId = list[0].DeviceId;
            } else if (list.length === 0) {
              this.filterDeviceId = "";
            }
            this.loadOnlineStatusForDropdown();
          }
        });
    },

    // 手动刷新摄像头列表和在线状态。
    refreshCameraList() {
      this.mqProbeStarted = {};
      this.loadCameraList();
    },
    
    // 按连接模式检测下拉框中的设备状态：MQ 设备主动查询人脸总数。
    loadOnlineStatusForDropdown() {
      if (!this.cameraList || this.cameraList.length === 0) return;
      const localEdgeCameras = this.cameraList.filter(item => item.ConnMode !== "Mq");
      const mqCameras = this.cameraList.filter(item => item.ConnMode === "Mq");

      if (localEdgeCameras.length > 0) {
        getKqtCameraOnlineDeviceIds()
          .then((res) => {
            if (res.data && res.data.success) {
              const onlineIds = res.data.response || [];
              localEdgeCameras.forEach((item) => {
                this.$set(item, 'IsOnline', onlineIds.includes(item.DeviceId));
              });
            }
          })
          .catch((err) => {
            console.error("获取工控机在线状态失败", err);
            localEdgeCameras.forEach((item) => this.$set(item, "IsOnline", false));
          });
      }

      mqCameras.forEach((camera) => {
        const cacheKey = `${this.schoolId}:${camera.DeviceId}`;
        const cached = this.mqOnlineCache[cacheKey];
        if (cached && Date.now() - cached.timestamp < this.mqOnlineCacheTtl) {
          this.$set(camera, "IsOnline", cached.isOnline);
          return;
        }
        if (this.mqProbeStarted[camera.DeviceId]) return;
        this.$set(this.mqProbeStarted, camera.DeviceId, true);
        this.probeMqCameraForDropdown(camera);
      });
    },

    // 通过 queryPersonStatistics 检测单台 MQ 摄像头是否可通信。
    probeMqCameraForDropdown(camera) {
      probeDevicePhysical({
        schoolId: this.schoolId,
        deviceId: camera.DeviceId,
        action: "query_person_stats"
      })
        .then((res) => {
          const body = res && res.data;
          if (!body || !body.success || !body.response) {
            this.$set(camera, "IsOnline", false);
            return;
          }

          let response = body.response;
          if (typeof response === "string") {
            try {
              response = JSON.parse(response);
            } catch (error) {
              response = null;
            }
          }
          const responseStatus = response
            && (response.status !== undefined ? response.status : response.Status);
          const isOnline = Number(responseStatus) === 0;
          this.$set(camera, "IsOnline", isOnline);
          this.$set(this.mqOnlineCache, `${this.schoolId}:${camera.DeviceId}`, {
            isOnline,
            timestamp: Date.now()
          });
        })
        .catch(() => {
          this.$set(camera, "IsOnline", false);
          this.$set(this.mqOnlineCache, `${this.schoolId}:${camera.DeviceId}`, {
            isOnline: false,
            timestamp: Date.now()
          });
        });
    },
    
    // 加载同步统计数据 (支持静默刷新)
    loadStats(isSilent = false) {
      if (!isSilent) this.statsLoading = true;
      const params = {
        schoolId: this.schoolId,
        departmentId: this.departmentId,
        deviceId: this.filterDeviceId || ""
      };
      const p1 = getCameraPersonSyncStats({ ...params, type: "dept" });
      
      // 人员同步统计参数根据子 Tab 决定
      const personParams = {
        schoolId: this.schoolId,
        departmentId: this.personSubTab === "teacher" ? this.schoolId : this.departmentId,
        deviceId: this.filterDeviceId || "",
        personType: this.personSubTab
      };
      const p2 = getCameraPersonSyncStats({ ...personParams, type: "person" });
      
      Promise.all([p1, p2]).then(([r1, r2]) => {
        if (!isSilent) this.statsLoading = false;
        if (r1.data && r1.data.success) {
          this.groupStats = r1.data.response;
        }
        if (r2.data && r2.data.success) {
          this.personStats = r2.data.response;
        }
      }).catch(() => {
        if (!isSilent) this.statsLoading = false;
      });
    },
    
    // 加载各 Tab 的同步明细列表 (支持静默刷新)
    loadDetails(isSilent = false) {
      if (!this.schoolId) return;
      if (!isSilent) this.detailsLoading = true;
      
      if (this.activeTab === "dept") {
        const params = {
          schoolId: this.schoolId,
          status: this.filterStatus,
          name: this.searchName,
          page: this.groupPage,
          intPageSize: this.pageSize,
          departmentId: this.personSubTab === "teacher" ? this.schoolId : this.departmentId,
          deviceId: this.filterDeviceId || "",
          personType: this.personSubTab
        };
        getCameraGroupDetailPage(params)
          .then((res) => {
            if (!isSilent) this.detailsLoading = false;
            if (res.data && res.data.success) {
              this.groupDetails = res.data.response.data;
              this.groupTotal = res.data.response.dataCount;
              // 数据拉取成功后，自动校验并确定是否需要启动后台静默轮询状态刷新
              this.checkAndStartPolling();
            }
          })
          .catch(() => {
            if (!isSilent) this.detailsLoading = false;
          });
      } else {
        const params = {
          schoolId: this.schoolId,
          status: this.filterStatus,
          name: this.searchName,
          page: this.personPage,
          intPageSize: this.pageSize,
          departmentId: this.personSubTab === "teacher" ? this.schoolId : this.departmentId,
          deviceId: this.filterDeviceId || "",
          personType: this.personSubTab
        };
        getCameraPersonDetailPage(params)
          .then((res) => {
            if (!isSilent) this.detailsLoading = false;
            if (res.data && res.data.success) {
              this.personDetails = res.data.response.data;
              this.personTotal = res.data.response.dataCount;
              // 数据拉取成功后，自动校验并确定是否需要启动后台静默轮询状态刷新
              this.checkAndStartPolling();
            }
          })
          .catch(() => {
            if (!isSilent) this.detailsLoading = false;
          });
      }
    },
    
    // 二级子Tab切换
    onSubTabChange() {
      this.personPage = 1;
      this.selectedPeople = [];
      if (this.$refs.personTable) {
        this.$refs.personTable.clearSelection();
      }
      this.loadDetails();
      this.loadStats();
    },

    // 过滤条件变动
    onFilterChange() {
      this.groupPage = 1;
      this.personPage = 1;
      this.loadDetails();
      this.loadStats();
    },

    // 手动刷新明细列表与状态统计
    handleRefresh() {
      this.loadDetails();
      this.loadStats();
      this.$message.success("同步列表及数据状态已刷新");
    },
    
    handleGroupPageChange(val) {
      this.groupPage = val;
      this.loadDetails();
    },
    
    handlePersonPageChange(val) {
      this.personPage = val;
      this.loadDetails();
    },
    
    handleSelectionChange(val) {
      this.selectedPeople = val;
    },
    
    // 单个同步部门年级
    syncSingleGroup(row) {
      if (!this.isDeviceOnline(row.DeviceId)) {
        this.$message.warning("当前摄像头离线，不能同步脸库");
        return;
      }
      this.confirmSingleSync(row, 'group', () => {
        this.syncLoading = true;
        const para = {
          schoolId: this.schoolId,
          deviceId: row.DeviceId,
          ids: [row.DepartmentId]
        };
        
        syncCameraGroups(para)
          .then((res) => {
            this.syncLoading = false;
            if (res.data && res.data.success) {
              this.$message.success("已成功派发同步指令，正在设备端执行，请稍后刷新列表查看结果。");
              this.loadDetails();
              this.loadStats();
            } else {
              this.$message.error(res.data.msg || "同步派发失败");
            }
          })
          .catch(() => {
            this.syncLoading = false;
            this.$message.error("同步失败");
          });
      });
    },

    // 单个删除部门年级 (从设备上清空年级分类脸库)
    deleteSingleGroup(row) {
      if (!this.isDeviceOnline(row.DeviceId)) {
        this.$message.warning("当前摄像头离线，不能删除脸库");
        return;
      }
      if (row.SyncStatus !== 1 || row.VendorGroupId === 0) return;
      this.$confirm(`确认从设备【${row.DeviceName}】上删除脸库【${row.DepartmentName}】及其底下所有人脸吗？`, "删除分类确认", {
        type: "warning",
        confirmButtonText: "确定删除",
        cancelButtonText: "取消"
      }).then(() => {
        this.syncLoading = true;
        const para = {
          schoolId: this.schoolId,
          deviceId: row.DeviceId,
          departmentId: row.DepartmentId
        };
        deleteCameraGroup(para)
          .then((res) => {
            this.syncLoading = false;
            if (res.data && res.data.success) {
              this.$message.success("已派发删除指令，正在清空设备端该分类脸库，请稍后刷新。");
              this.loadDetails();
              this.loadStats();
            } else {
              this.$message.error(res.data.msg || "指令发送失败");
            }
          })
          .catch(() => {
            this.syncLoading = false;
            this.$message.error("网络异常，删除失败");
          });
      }).catch(() => {});
    },
    
    // 批量同步部门年级
    handleBatchSyncGroups() {
      if (!this.isCurrentDeviceOnline) {
        this.$message.warning("当前摄像头离线，不能同步脸库");
        return;
      }
      const scopeName = this.departmentName || "当前部门";
      
      // 获取即将同步的具体摄像头名称列表
      const targetCameras = this.filterDeviceId
        ? (this.cameraList || []).filter(c => c.DeviceId === this.filterDeviceId)
        : [];
      
      if (targetCameras.length === 0) {
        this.$message.warning("请先选择一台下发摄像头！");
        return;
      }

      // 确认名单：范围内年级，并带上学校教职工脸库。最后修改：2026-10-14
      let targetGrades = (this.selectedGrades || []).slice();
      if (targetGrades.length === 0) {
        targetGrades = Array.from(new Set(this.groupDetails.map(g => g.DepartmentName).filter(Boolean)));
      }
      const staffLabel = (this.schoolName || "当前学校") + "（教职工）";
      if (!targetGrades.some(name => name && String(name).indexOf("（教职工）") >= 0)) {
        targetGrades.unshift(staffLabel);
      }

      // 转换为响应式卡片勾选数据
      const dialogCameras = targetCameras.map(c => {
        const isOnline = c.IsOnline === true;
        return {
          DeviceId: c.DeviceId,
          DeviceName: c.DeviceName,
          IsOnline: c.IsOnline,
          isSelected: isOnline // 在线默认勾选，离线默认不勾选
        };
      });

      this.syncDialog = {
        visible: true,
        title: "同步确认",
        type: "group",
        scopeName: scopeName,
        schoolName: this.schoolName,
        grades: targetGrades,
        cameras: dialogCameras,
        persons: []
      };
    },

    // 切换弹框中摄像头选中状态
    toggleCameraSelect(camera) {
      if (!camera || camera.IsOnline !== true) {
        this.$message.warning("设备离线，不能执行同步或删除操作");
        return;
      }
      camera.isSelected = !camera.isSelected;
    },

    // 提交弹框同步指令
    submitSyncConfirm() {
      const selectedCamObjects = this.syncDialog.cameras.filter(c => c.isSelected);
      
      if (selectedCamObjects.length === 0) {
        this.$message.warning("请至少勾选一台接收设备进行同步！");
        return;
      }

      // 提取选中的设备中处于离线状态的摄像头
      const offlineCams = selectedCamObjects.filter(c => c.IsOnline !== true);

      if (offlineCams.length > 0) {
        const offlineNames = offlineCams.map(c => c.DeviceName).join("、");
        this.$confirm(
          `检测到选中的设备【${offlineNames}】处于离线状态。由于同步指令是通过网关即时中转，设备在离线期间无法收到并执行该指令。确定要继续强行同步吗？`,
          "设备离线警告",
          {
            confirmButtonText: "确定同步",
            cancelButtonText: "取消",
            type: "warning"
          }
        ).then(() => {
          this.executeSyncPost(selectedCamObjects.map(c => c.DeviceId));
        }).catch(() => {
          // 用户取消下发，不作处理，保留弹窗
        });
      } else {
        this.executeSyncPost(selectedCamObjects.map(c => c.DeviceId));
      }
    },

    // 执行真实的同步/推送 API 请求
    executeSyncPost(selectedCams) {
      this.syncLoading = true;
      
      if (this.syncDialog.type === 'group') {
        const promises = selectedCams.map(devId => {
          const para = {
            schoolId: this.schoolId,
            deviceId: devId,
            departmentId: this.departmentId
          };
          return syncCameraGroups(para);
        });

        Promise.all(promises)
          .then((results) => {
            this.syncLoading = false;
            this.syncDialog.visible = false;
            const successCount = results.filter(r => r.data && r.data.success).length;
            if (successCount > 0) {
              this.$message.success(`已成功向选中的 ${successCount} 台摄像头派发同步指令`);
              this.loadDetails();
              this.loadStats();
            } else {
              this.$message.error("下发同步指令失败");
            }
          })
          .catch(() => {
            this.syncLoading = false;
            this.$message.error("网关派发指令网络异常");
          });
      } else {
        const promises = selectedCams.map(devId => {
          const para = {
            schoolId: this.schoolId,
            deviceId: devId,
            departmentId: this.personSubTab === "teacher" ? this.schoolId : this.departmentId,
            personType: this.personSubTab
          };
          return syncCameraPersons(para);
        });

        Promise.all(promises)
          .then((results) => {
            this.syncLoading = false;
            this.syncDialog.visible = false;
            const successCount = results.filter(r => r.data && r.data.success).length;
            if (successCount > 0) {
              this.$message.success(`已成功向选中的 ${successCount} 台摄像头派发人脸推送指令`);
              this.loadDetails();
              this.loadStats();
            } else {
              this.$message.error("推送指令下发失败");
            }
          })
          .catch(() => {
            this.syncLoading = false;
            this.$message.error("人脸下发网络异常");
          });
      }
    },
    
    // 单个同步人员人脸
    syncSinglePerson(row) {
      if (!this.isDeviceOnline(row.DeviceId)) {
        this.$message.warning("当前摄像头离线，不能同步人脸");
        return;
      }
      if (!row.HasPhoto) {
        this.$message.warning("该人员未上传照片，无法下发至摄像头！");
        return;
      }
      this.confirmSingleSync(row, 'person', () => {
        this.syncLoading = true;
        const para = {
          schoolId: this.schoolId,
          deviceId: row.DeviceId,
          ids: [row.PersonId]
        };
        
        syncCameraPersons(para)
          .then((res) => {
            this.syncLoading = false;
            if (res.data && res.data.success) {
              this.$message.success("人脸同步指令发送成功，正在通过网关中转下发...");
              this.loadDetails();
              this.loadStats();
            } else {
              this.$message.error(res.data.msg || "同步失败");
            }
          })
          .catch(() => {
            this.syncLoading = false;
            this.$message.error("同步失败");
          });
      });
    },

    // 统一校验单行同步前置状态并弹出确认 (防止已成功状态误触产生重复日志)
    confirmSingleSync(row, type, proceedCallback) {
      const cam = this.cameraList.find(c => c.DeviceId === row.DeviceId);
      const deviceName = row.DeviceName || (cam ? cam.DeviceName : "目标设备");
      const text = type === 'group' ? '年级分类' : '人员人脸';

      if (row.SyncStatus === 1) {
        this.$confirm(
          `该${text}在设备【${deviceName}】上已同步成功。确定要重新强制下发一次吗？`,
          "重新同步确认",
          {
            confirmButtonText: "确定重新下发",
            cancelButtonText: "取消",
            type: "warning"
          }
        ).then(() => {
          proceedCallback();
        }).catch(() => {
          // 用户取消
        });
      } else {
        this.$confirm(
          `确认向设备【${deviceName}】同步该${text}吗？`,
          "同步确认",
          {
            confirmButtonText: "确定同步",
            cancelButtonText: "取消",
            type: "info"
          }
        ).then(() => {
          proceedCallback();
        }).catch(() => {
          // 用户取消
        });
      }
    },

    // 单个删除人员人脸
    deleteSinglePerson(row) {
      if (!this.isDeviceOnline(row.DeviceId)) {
        this.$message.warning("当前摄像头离线，不能删除人脸");
        return;
      }
      if (row.SyncStatus !== 1) return;
      this.$confirm(`确认将人员【${row.PersonName}】的人脸从设备【${row.DeviceName}】上删除吗？`, "删除人脸确认", {
        type: "warning",
        confirmButtonText: "确定删除",
        cancelButtonText: "取消"
      }).then(() => {
        this.syncLoading = true;
        const para = {
          schoolId: this.schoolId,
          deviceId: row.DeviceId,
          ids: [row.PersonId]
        };
        deleteCameraPersons(para)
          .then((res) => {
            this.syncLoading = false;
            if (res.data && res.data.success) {
              this.$message.success("已向摄像头发送人脸删除指令，正在处理...");
              this.loadDetails();
              this.loadStats();
            } else {
              this.$message.error(res.data.msg || "人脸删除失败");
            }
          })
          .catch(() => {
            this.syncLoading = false;
            this.$message.error("网络异常");
          });
      }).catch(() => {});
    },
    
    // 批量推送未同步人员人脸
    handleBatchSyncPersons() {
      if (!this.isCurrentDeviceOnline) {
        this.$message.warning("当前摄像头离线，不能同步人脸");
        return;
      }
      if (!this.schoolId) {
        this.$message.warning("请先从左侧选择学校/机构节点！");
        return;
      }

      const scopeName = this.personSubTab === "teacher" ? (this.schoolName + "全体教师") : (this.departmentName || "当前部门");
      
      // 获取即将同步的具体摄像头名称列表
      const targetCameras = this.filterDeviceId
        ? (this.cameraList || []).filter(c => c.DeviceId === this.filterDeviceId)
        : [];
      
      if (targetCameras.length === 0) {
        this.$message.warning("请先选择一台下发摄像头！");
        return;
      }

      // 转换为响应式卡片勾选数据
      const dialogCameras = targetCameras.map(c => {
        const isOnline = c.IsOnline === true;
        return {
          DeviceId: c.DeviceId,
          DeviceName: c.DeviceName,
          IsOnline: c.IsOnline,
          isSelected: isOnline
        };
      });

      // 开启局部 loading，异步加载所有待下发或下发失败的师生名单
      this.syncLoading = true;
      const params = {
        schoolId: this.schoolId,
        status: "2", // 过滤待同步与已失败人员
        name: "",
        page: 1,
        intPageSize: 2000, // 设定较大分页大小，一次性获取全部待同步人员
        departmentId: this.personSubTab === "teacher" ? this.schoolId : this.departmentId,
        deviceId: this.filterDeviceId || "",
        personType: this.personSubTab
      };

      getCameraPersonDetailPage(params)
        .then((res) => {
          this.syncLoading = false;
          if (res.data && res.data.success) {
            const allList = res.data.response.data || [];
            // 自动过滤掉无照片的人员，只保留有照片的人员用于人脸同步
            const list = allList.filter(p => p.HasPhoto === true);
            
            if (list.length === 0) {
              this.$message.info("当前节点下暂无有照片的待同步或推送失败人员！请先为人员上传照片。");
              return;
            }
            // 清空检索框，加载人员名单
            this.personListDialog.searchQuery = "";
            this.syncDialog = {
              visible: true,
              title: "同步确认",
              type: "person",
              scopeName: scopeName,
              schoolName: this.schoolName,
              grades: [],
              cameras: dialogCameras,
              persons: list // 待下发的人员列表
            };
          } else {
            this.$message.error(res.data.msg || "获取待下发人员名单失败");
          }
        })
        .catch((err) => {
          this.syncLoading = false;
          console.error(err);
          this.$message.error("获取待下发人员名单时发生网络异常");
        });
    },
    
    // 同步勾选的多个人员
    handleSyncSelectedPeople() {
      if (!this.isCurrentDeviceOnline) {
        this.$message.warning("当前摄像头离线，不能同步人脸");
        return;
      }
      const selectedCount = this.selectedPeople.length;
      if (selectedCount === 0) return;

      // 自动过滤掉其中未上传照片的人员
      const validSelected = this.selectedPeople.filter(p => p.HasPhoto === true);
      if (validSelected.length === 0) {
        this.$message.warning("所勾选的人员均未上传人脸照片，无法同步！");
        return;
      }

      const skippedCount = selectedCount - validSelected.length;
      const skipTip = skippedCount > 0 ? `（其中已自动跳过 ${skippedCount} 名未上传照片人员）` : '';

      this.$confirm(`确认将已勾选的 ${validSelected.length} 名人员的人脸数据同步至当前设备吗？${skipTip}`, "批量同步确认", {
        type: "info",
        confirmButtonText: "确定同步",
        cancelButtonText: "取消"
      }).then(() => {
        this.syncLoading = true;
        // 提取勾选人员中合格者的 PersonId 列表
        const personIds = validSelected.map(p => p.PersonId);
        const para = {
          schoolId: this.schoolId,
          deviceId: this.filterDeviceId || "",
          ids: personIds
        };
        
        syncCameraPersons(para)
          .then((res) => {
            this.syncLoading = false;
            if (res.data && res.data.success) {
              this.$message.success("已成功对勾选的人员发起人脸下发指令");
              this.loadDetails();
              this.loadStats();
              this.$refs.personTable.clearSelection();
            } else {
              this.$message.error(res.data.msg || "下发失败");
            }
          })
          .catch(() => {
            this.syncLoading = false;
          });
      }).catch(() => {
        // 用户取消
      });
    },

    // 批量删除已勾选人员人脸
    handleDeleteSelectedPeople() {
      if (!this.isCurrentDeviceOnline) {
        this.$message.warning("当前摄像头离线，不能删除人脸");
        return;
      }
      const selectedCount = this.selectedPeople.length;
      if (selectedCount === 0) return;
      
      this.$confirm(`确认将已勾选的 ${selectedCount} 名人员的人脸从当前设备中批量删除吗？`, "批量删除确认", {
        type: "warning",
        confirmButtonText: "确定删除",
        cancelButtonText: "取消"
      }).then(() => {
        this.syncLoading = true;
        if (!this.filterDeviceId) {
          this.syncLoading = false;
          this.$message.warning("批量删除设备人脸前，请先在上方选择指定的“下发摄像头”！");
          return;
        }

        const personIds = this.selectedPeople.map(p => p.PersonId);
        const para = {
          schoolId: this.schoolId,
          deviceId: this.filterDeviceId,
          ids: personIds
        };
        
        deleteCameraPersons(para)
          .then((res) => {
            this.syncLoading = false;
            if (res.data && res.data.success) {
              this.$message.success(`成功向指定设备派发了 ${selectedCount} 个人脸删除任务`);
              this.loadDetails();
              this.loadStats();
              this.$refs.personTable.clearSelection();
            } else {
              this.$message.error(res.data.msg || "批量删除失败");
            }
          })
          .catch(() => {
            this.syncLoading = false;
            this.$message.error("网络异常");
          });
      }).catch(() => {});
    },
    
    // 获取同步状态 Tag 样式
    getSyncStatusValue(row) {
      if (!row) return 0;
      const value = row.SyncStatus !== undefined
        ? row.SyncStatus
        : row.syncStatus;
      const status = Number(value);
      return Number.isNaN(status) ? 0 : status;
    },

    getStatusTagType(row) {
      if (!row) return "info";
      const status = this.getSyncStatusValue(row);
      const errMsg = row.ErrorMessage || "";
      if (errMsg.includes("正在从设备上删除") || errMsg.includes("正在清空中")) {
        return "warning";
      }
      if (status === 1) return "success";
      if (status === 2) return "danger";
      return "info";
    },
    
    // 获取同步状态智能描述
    getStatusText(row) {
      if (!row) return "";
      const status = this.getSyncStatusValue(row);
      const errMsg = row.ErrorMessage || "";
      const isWaiting = errMsg.includes("等待网关");
      const isDeleting = errMsg.includes("正在从设备上删除") || errMsg.includes("正在清空中");

      if (isDeleting) {
        return "正在删除...";
      }
      if (status === 1) {
        return isWaiting ? "已成功 (更新中)" : "已同步成功";
      }
      if (status === 2) {
        return isWaiting ? "同步中 (重试)" : "同步失败";
      }
      return isWaiting ? "正在同步中" : "待同步";
    },

    // 是否需要显示状态 Loading 图标
    showStatusLoading(row) {
      if (!row) return false;
      const status = this.getSyncStatusValue(row);
      const errMsg = row.ErrorMessage || "";
      return status === 0 || errMsg.includes("等待网关") || errMsg.includes("正在从设备上删除") || errMsg.includes("正在清空中");
    },

    // 判定摄像头是否在线
    isDeviceOnline(deviceId) {
      if (!this.cameraList) return false;
      const cam = this.cameraList.find(c => c.DeviceId === deviceId);
      return cam ? cam.IsOnline === true : false;
    },
    
    // 获取人员首字以作为头像展示
    getInitials(name) {
      if (!name) return "?";
      return name.trim().charAt(0);
    },
    
    // 根据姓名首字生成哈希色调，以维持固定的优雅背景配色
    getAvatarBg(name) {
      if (!name) return "#3b82f6";
      const colors = [
        "#3b82f6", // 经典蓝
        "#10b981", // 翡翠绿
        "#f97316", // 暖阳橙
        "#8b5cf6", // 罗兰紫
        "#ec4899", // 玫瑰粉
        "#06b6d4"  // 碧波蓝
      ];
      let charCodeSum = 0;
      for (let i = 0; i < name.length; i++) {
        charCodeSum += name.charCodeAt(i);
      }
      return colors[charCodeSum % colors.length];
    },

    // 打开统计明细弹窗
    openStatDetail(type, status) {
      if (!this.schoolId) {
        this.$message.warning("请先从左侧选择学校/机构节点！");
        return;
      }
      this.statDetailDialog.type = type;
      this.statDetailDialog.status = status;
      this.statDetailDialog.page = 1;
      this.statDetailDialog.searchQuery = "";
      this.statDetailDialog.dataList = [];
      this.statDetailDialog.total = 0;

      // 获取当前过滤的摄像头名称
      let currentDeviceName = "全部摄像头设备";
      if (this.filterDeviceId) {
        const cam = this.cameraList.find(c => c.DeviceId === this.filterDeviceId);
        if (cam) {
          currentDeviceName = cam.DeviceName;
        }
      }
      this.statDetailDialog.deviceName = currentDeviceName;

      // 组装标题
      let titlePrefix = type === "dept" ? "脸库分类" : (this.personSubTab === "teacher" ? "教师人脸" : "学生人脸");
      let titleSuffix = '预计总人次';
      if (type === 'dept') {
        titleSuffix = '预计总映射';
      }
      if (status === '1') {
        titleSuffix = type === 'dept' ? '同步成功' : '已写入成功';
      } else if (status === '2') {
        titleSuffix = type === 'dept' ? '未同步/失败' : '待同步/失败';
      }
      this.statDetailDialog.title = `${titlePrefix}${titleSuffix}明细`;
      this.statDetailDialog.visible = true;

      this.loadStatDetailList();
    },

    // 加载统计明细数据
    loadStatDetailList() {
      this.statDetailDialog.loading = true;
      const params = {
        schoolId: this.schoolId,
        status: this.statDetailDialog.status,
        name: this.statDetailDialog.searchQuery,
        page: this.statDetailDialog.page,
        intPageSize: this.statDetailDialog.pageSize,
        departmentId: (this.statDetailDialog.type === "person" && this.personSubTab === "teacher") ? this.schoolId : this.departmentId,
        deviceId: this.filterDeviceId || "",
        personType: this.statDetailDialog.type === "person" ? this.personSubTab : ""
      };

      const request = this.statDetailDialog.type === 'dept' 
        ? getCameraGroupDetailPage(params)
        : getCameraPersonDetailPage(params);

      request.then(res => {
        this.statDetailDialog.loading = false;
        if (res.data && res.data.success) {
          this.statDetailDialog.dataList = res.data.response.data || [];
          this.statDetailDialog.total = res.data.response.dataCount || 0;
        } else {
          this.$message.error(res.data.msg || "获取明细数据失败");
        }
      }).catch(err => {
        this.statDetailDialog.loading = false;
        console.error(err);
        this.$message.error("获取明细数据发生网络异常");
      });
    },

    // 统计明细弹窗检索
    handleStatDetailSearch() {
      this.statDetailDialog.page = 1;
      this.loadStatDetailList();
    },

    // 统计明细弹窗分页变更
    handleStatDetailPageChange(val) {
      this.statDetailDialog.page = val;
      this.loadStatDetailList();
    },

    // 打开同步历史弹窗
    openSyncLogDialog() {
      if (!this.schoolId) {
        this.$message.warning("请先从左侧选择学校/机构节点！");
        return;
      }
      this.logDialog.visible = true;
      this.logDialog.page = 1;
      this.loadSyncLogPage();
    },

    // 分页加载同步主日志
    loadSyncLogPage() {
      if (!this.schoolId) return;
      this.logDialog.loading = true;
      const params = {
        schoolId: this.schoolId,
        page: this.logDialog.page,
        intPageSize: this.logDialog.pageSize
      };
      getKqtCameraSyncLogPage(params)
        .then((res) => {
          this.logDialog.loading = false;
          if (res.data && res.data.success) {
            this.logDialog.list = res.data.response.data || [];
            this.logDialog.total = res.data.response.dataCount || 0;
          } else {
            this.$message.error(res.data.msg || "加载同步历史失败");
          }
        })
        .catch(() => {
          this.logDialog.loading = false;
          this.$message.error("网络异常");
        });
    },

    // 主日志翻页
    handleLogPageChange(val) {
      this.logDialog.page = val;
      this.loadSyncLogPage();
    },

    // 转换日志同步类型标签
    getSyncTypeTag(type) {
      const tags = {
        0: "primary",
        1: "success",
        2: "warning",
        3: "danger"
      };
      return tags[type] || "info";
    },

    // 转换日志同步类型文本说明
    getSyncTypeText(type) {
      const texts = {
        0: "同步脸库分类",
        1: "推送人员人脸",
        2: "删除脸库分类",
        3: "删除人员人脸"
      };
      return texts[type] || "未知类型";
    },

    // 转换操作明细指令动作
    getActionText(action) {
      const texts = {
        "add_group": "同步/创建分类",
        "modify_group": "重命名分类",
        "remove_group": "删除分类",
        "add_person": "推送/上传人脸",
        "delete_person": "删除人脸"
      };
      return texts[action] || action;
    },

    // 查看明细
    viewLogDetail(row) {
      this.detailDialog.logId = row.Id;
      this.detailDialog.syncType = row.SyncType;
      this.detailDialog.page = 1;
      this.detailDialog.visible = true;
      this.loadDetailLogPage();
    },

    // 加载明细指令分页
    loadDetailLogPage() {
      if (!this.detailDialog.logId) return;
      this.detailDialog.loading = true;
      const params = {
        logId: this.detailDialog.logId,
        page: this.detailDialog.page,
        intPageSize: this.detailDialog.pageSize
      };
      getKqtCameraSyncLogDetailPage(params)
        .then((res) => {
          this.detailDialog.loading = false;
          if (res.data && res.data.success) {
            this.detailDialog.list = res.data.response.data || [];
            this.detailDialog.total = res.data.response.dataCount || 0;
          } else {
            this.$message.error(res.data.msg || "加载指令明细失败");
          }
        })
        .catch(() => {
          this.detailDialog.loading = false;
          this.$message.error("网络异常");
        });
    },

    // 明细翻页
    handleDetailLogPageChange(val) {
      this.detailDialog.page = val;
      this.loadDetailLogPage();
    },

    // 检查当前列表是否有正在进行中的任务，并按需开启静默轮询自动刷新
    checkAndStartPolling() {
      // 检查当前激活的 Tab 列表里是否有状态为“正在进行/等待网关”的行
      const hasActiveJob = this.activeTab === "dept"
        ? this.groupDetails.some(row => this.showStatusLoading(row))
        : this.personDetails.some(row => this.showStatusLoading(row));

      if (hasActiveJob) {
        if (!this.listPollTimer) {
          // 每隔 3 秒自动进行一次静默数据拉取，刷新最新执行状态
          this.listPollTimer = setInterval(() => {
            this.loadDetails(true);
            this.loadStats(true);
          }, 3000);
        }
      } else {
        // 如果已经没有进行中的任务，自动关闭定时器以节省服务器和网络资源
        this.stopListPolling();
      }
    },

    // 停止并清理主列表自动轮询定时器
    stopListPolling() {
      if (this.listPollTimer) {
        clearInterval(this.listPollTimer);
        this.listPollTimer = null;
      }
      this.mqProbeStarted = {};
    }
  },
  watch: {
    activeTab() {
      this.filterStatus = "";
      this.loadDetails();
    }
  },
  // 组件销毁前彻底清理所有轮询定时器，防止后台内存泄漏与冗余请求
  deactivated() {
    this.stopListPolling();
    if (this.deviceAuditDialog.timer) {
      clearInterval(this.deviceAuditDialog.timer);
      this.deviceAuditDialog.timer = null;
    }
  },
  beforeDestroy() {
    this.stopListPolling();
    if (this.deviceAuditDialog.timer) {
      clearInterval(this.deviceAuditDialog.timer);
    }
  }
};
</script>

<style scoped>
/* 三个 Tab 内容区统一高度，切换时弹窗不跳动 */
.physical-audit-pane {
  height: 372px;
  box-sizing: border-box;
}

.physical-audit-stats-pane {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 20px;
}

.physical-audit-list-pane {
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.physical-audit-list-pane .pagination-wrap {
  flex-shrink: 0;
  margin-top: auto;
}

.status-dot-mini {
  display: inline-block;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  margin-left: 2px;
  flex-shrink: 0;
}
.status-dot-mini.is-online {
  background-color: #10b981;
  box-shadow: 0 0 6px rgba(16, 185, 129, 0.7);
}
.status-dot-mini.is-offline {
  background-color: #ef4444;
  box-shadow: 0 0 4px rgba(239, 68, 68, 0.4);
}
::v-deep .el-pagination {
  background: transparent !important;
  box-shadow: none !important;
  padding: 0 !important;
  margin-top: 10px !important;
  margin-bottom: 10px !important;
}
.clickable-num {
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  display: inline-block; /* 保证 transform 缩放生效 */
}
.clickable-num:hover {
  text-decoration: underline;
  transform: scale(1.08);
  opacity: 0.85;
}

/* 最外层无样式的包裹容器，确保 el-dialog 在 flex 外占位不影响布局 */
.camera-sync-page-wrapper {
  height: 100%;
  max-height: 100%;
  min-height: 0;
  overflow: hidden;
  box-sizing: border-box;
}

/* 全局布局重塑 */
.camera-sync-layout {
  padding: 0;
  display: flex;
  flex-direction: column;
  height: 100%;
  box-sizing: border-box;
  background-color: #f8fafc;
}

.camera-sync-layout > .sync-topbar {
  margin: 10px 12px 0;
  flex-shrink: 0;
}

.camera-sync-layout > .sync-main {
  flex: 1;
  min-height: 0;
  padding: 10px 12px 12px;
  overflow: hidden;
}

/* 顶栏：统计和摄像头一行，范围说明单独一行。最后修改：2026-10-14 */
.sync-head {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 6px;
  margin-bottom: 8px;
  flex-shrink: 0;
  min-width: 0;
}
.sync-head-row {
  display: flex;
  align-items: center;
  gap: 8px 10px;
  min-width: 0;
}
.sync-metrics {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 0 1 auto;
  min-width: 0;
}
.sync-head-tip {
  display: flex;
  align-items: flex-start;
  gap: 4px;
  margin: 0;
  font-size: 12px;
  line-height: 1.5;
  color: #d97706;
}
.sync-head-tools {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  margin-left: auto;
}
.device-select {
  width: 200px;
}
.sync-head-tools ::v-deep .el-select .el-input__inner {
  border-radius: 8px;
  border: 1px solid #e2e8f0;
}
.guide-block + .guide-block {
  margin-top: 16px;
}
.guide-steps {
  margin: 10px 0 0;
  padding-left: 18px;
  color: #334155;
  font-size: 13px;
  line-height: 1.8;
}
.guide-text {
  margin: 10px 0 0;
  color: #334155;
  font-size: 13px;
  line-height: 1.7;
}
.toolbar-sub-tabs {
  flex-shrink: 0;
}

/* 顶栏 Tab */
.sync-topbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0;
  padding: 8px 16px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.04);
}
.sync-topbar-title {
  font-size: 16px;
  font-weight: 700;
  color: #1e293b;
  display: flex;
  align-items: center;
}
.pulse-dot {
  width: 8px;
  height: 8px;
  background-color: #10b981;
  border-radius: 50%;
  margin-right: 10px;
  box-shadow: 0 0 10px rgba(16, 185, 129, 0.8);
  animation: pulse-animation 2.5s infinite ease-in-out;
}
@keyframes pulse-animation {
  0% { transform: scale(0.85); opacity: 0.6; box-shadow: 0 0 4px rgba(16, 185, 129, 0.4); }
  50% { transform: scale(1.15); opacity: 1; box-shadow: 0 0 12px rgba(16, 185, 129, 0.8); }
  100% { transform: scale(0.85); opacity: 0.6; box-shadow: 0 0 4px rgba(16, 185, 129, 0.4); }
}
.sync-tabs {
  display: flex;
  gap: 6px;
  background: rgba(15, 23, 42, 0.04);
  padding: 4px;
  border-radius: 8px;
}
.sync-tab {
  background: transparent;
  border: none;
  padding: 8px 18px;
  border-radius: 6px;
  font-family: "Helvetica Neue", Helvetica, "PingFang SC", "Hiragino Sans GB", "Microsoft YaHei", "微软雅黑", Arial, sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: #64748b;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}
.sync-tab:hover {
  color: #3b82f6;
  background: rgba(59, 130, 246, 0.06);
}
.sync-tab.is-active {
  background: #ffffff;
  color: #3b82f6;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

/* 双栏主布局：对齐 TbBasicSync query-layout */
.sync-main {
  margin-top: 0;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: transparent;
}

.query-layout {
  flex: 1;
  min-height: 0;
  display: flex;
  align-items: stretch;
  gap: 20px;
  overflow: hidden;
}

.dept-filter-pane {
  position: relative;
  flex: 0 0 280px;
  width: 280px;
  min-width: 280px;
  flex-shrink: 0;
  height: 100%;
  max-height: 100%;
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  border-left: 4px solid #2563eb;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.02);
  overflow: visible !important;
  box-sizing: border-box;
  transition: all 0.25s ease;
}

/* 收起部门树：宽度归零，仅保留展开浮钮。最后修改：2026-08-31 */
.dept-filter-pane.is-collapsed {
  flex: 0 0 0 !important;
  width: 0 !important;
  min-width: 0 !important;
  max-width: 0 !important;
  padding: 0 !important;
  border: none !important;
  margin: 0 !important;
  overflow: visible !important;
  box-shadow: none !important;
  background: transparent !important;
}

.dept-filter-pane.is-collapsed .dept-filter-header {
  display: none !important;
}

.dept-filter-pane.is-collapsed ::v-deep .department-pane {
  border: none !important;
  background: transparent !important;
  box-shadow: none !important;
}

.dept-filter-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 14px 16px;
  font-weight: 600;
  font-size: 14px;
  color: #1e293b;
  border-bottom: 1px solid #f1f5f9;
  flex-shrink: 0;
}
.dept-filter-header i {
  color: #2563eb;
  font-size: 16px;
}

.dept-filter-pane ::v-deep .department-pane {
  flex: 1;
  height: auto;
  min-height: 0;
  border: none;
  border-radius: 0;
  box-shadow: none;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  background: transparent;
}

.dept-filter-pane ::v-deep .department-pane .el-tree {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: auto;
  background: transparent;
  scrollbar-width: thin;
  scrollbar-color: #94a3b8 #f1f5f9;
}
.dept-filter-pane ::v-deep .el-tree-node__content {
  height: 36px;
  border-radius: 6px;
  margin-bottom: 2px;
  transition: all 0.2s ease;
}
.dept-filter-pane ::v-deep .el-tree-node__content:hover {
  background-color: rgba(37, 99, 235, 0.06) !important;
  color: #2563eb;
}
.dept-filter-pane ::v-deep .el-tree-node.is-current > .el-tree-node__content {
  background-color: rgba(37, 99, 235, 0.1) !important;
  color: #2563eb;
  font-weight: 700;
}
.dept-filter-pane ::v-deep .el-tree-node__expand-icon {
  font-size: 12px;
  color: #94a3b8;
  transition: transform 0.25s;
}
.dept-filter-pane ::v-deep .el-tree-node__expand-icon.expanded {
  transform: rotate(90deg);
  color: #2563eb;
}

.query-main {
  flex: 1;
  min-width: 0;
  min-height: 0;
  height: 100%;
  /* 禁止整栏外滚，否则 flex:1 表体拿不到确定高度 */
  overflow: hidden;
  padding-right: 4px;
  display: flex;
  flex-direction: column;
}

.panel-desc {
  font-size: 12.5px;
  line-height: 1.6;
  color: #334155;
}
.desc-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 6px;
  font-size: 10.5px;
  font-weight: 700;
  margin-right: 8px;
  color: #ffffff;
}
.badge-primary { background: #4f46e5; box-shadow: 0 2px 6px rgba(79, 70, 229, 0.2); }
.badge-warning { background: #d97706; box-shadow: 0 2px 6px rgba(217, 119, 6, 0.2); }

/* 统计芯片跟在顶栏里，不再单独占三张大卡。最后修改：2026-10-14 */

/* 手机端：顶栏/设备条/工具栏换行，表体随整页滑。最后修改：2026-08-31 */
@media (max-width: 768px) {
  .camera-sync-page-wrapper,
  .camera-sync-layout {
    height: auto;
    max-height: none;
    overflow: visible;
  }

  .camera-sync-layout > .sync-topbar {
    margin: 8px 8px 0;
    padding: 10px 12px;
    flex-wrap: wrap;
    gap: 8px;
  }

  .camera-sync-layout > .sync-main {
    padding: 10px 8px 16px;
    height: auto;
    overflow: visible;
  }

  .sync-topbar-title {
    font-size: 15px;
    width: 100%;
  }

  .sync-topbar > div {
    width: 100%;
    flex-wrap: wrap;
    gap: 8px;
  }

  .sync-tabs {
    flex: 1;
    min-width: 0;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    flex-wrap: nowrap;
  }

  .sync-tab {
    white-space: nowrap;
    flex-shrink: 0;
    padding: 8px 12px;
  }

  .query-layout {
    flex-direction: column;
    height: auto;
    overflow: visible;
    gap: 10px;
  }

  .dept-filter-pane {
    width: 100%;
    min-width: 0;
    max-width: 100%;
    max-height: 280px;
    height: auto;
  }

  .dept-filter-pane.is-collapsed {
    display: none !important;
  }

  .query-main {
    height: auto;
    overflow: visible;
    padding-right: 0;
  }

  .sync-head-row {
    flex-wrap: wrap;
    align-items: stretch;
  }

  .sync-metrics {
    width: 100%;
    flex-wrap: wrap;
  }

  .sync-head-tools {
    width: 100%;
    margin-left: 0;
    flex-wrap: wrap;
  }

  .device-select {
    width: 100%;
  }

  .filter-toolbar-inner {
    flex-wrap: wrap;
  }

  .filter-toolbar-left,
  .filter-toolbar-right {
    width: 100%;
    flex-wrap: wrap;
  }

  .filter-toolbar-right {
    margin-left: 0;
    justify-content: flex-start;
  }

  .filter-status-select,
  .filter-search-input {
    width: 100%;
    max-width: 100%;
  }

  .query-main {
    height: auto;
    overflow: visible;
  }

  .detail-container {
    flex: none;
    min-height: 360px;
    height: 480px;
    overflow: hidden;
  }

  .detail-table-panel,
  .detail-table-host {
    height: auto !important;
    min-height: 0;
    flex: 1 1 auto;
    overflow: hidden;
  }

  .pagination-wrap {
    justify-content: flex-end;
    overflow-x: auto;
  }
}
.stat-card {
  display: inline-flex;
  align-items: baseline;
  gap: 8px;
  padding: 6px 12px;
  border-radius: 8px;
  border: 1px solid rgba(0, 0, 0, 0.05);
  white-space: nowrap;
  flex-shrink: 0;
}
.stat-label {
  font-size: 12px;
  font-weight: 600;
}
.stat-value {
  font-size: 16px;
  font-weight: 700;
  line-height: 1.2;
}
.stat-value .unit {
  font-size: 12px;
  margin-left: 4px;
  font-weight: 500;
}
.stat-rate {
  font-size: 11px;
  font-weight: 600;
  opacity: 0.85;
  margin-left: 8px;
}
.stat-icon-bg {
  display: none;
}
.stat-blue {
  background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
  color: #1e40af;
}
.stat-emerald {
  background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
  color: #166534;
}
.stat-orange {
  background: linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%);
  color: #c2410c;
}
.stat-card-clickable {
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}
.stat-card-clickable:hover {
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.08);
}

.notice-banner {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 13px;
  margin-bottom: 10px;
  flex-shrink: 0;
}
.notice-info {
  background: #eff6ff;
  color: #1d4ed8;
  border: 1px solid #bfdbfe;
}

/* 筛选条件工具栏 */
.filter-toolbar {
  margin-bottom: 8px;
  border-bottom: 1px solid #f1f5f9;
  padding-bottom: 8px;
  flex-shrink: 0;
}
.filter-toolbar-inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px 12px;
  flex-wrap: wrap;
}
.filter-toolbar-left,
.filter-toolbar-right {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  min-width: 0;
}
.filter-toolbar-right {
  margin-left: auto;
  flex-shrink: 0;
}
.filter-status-select {
  width: 160px;
}
.filter-search-input {
  width: 200px;
}
.filter-toolbar ::v-deep .el-select .el-input__inner,
.filter-toolbar ::v-deep .el-input .el-input__inner {
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  background-color: #ffffff;
  transition: all 0.2s ease;
}
.filter-toolbar ::v-deep .el-select .el-input__inner:focus,
.filter-toolbar ::v-deep .el-input .el-input__inner:focus {
  border-color: #6366f1;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.12);
}

/* 列表详情容器：吃满右侧剩余高度，分页钉在底部。最后修改：2026-08-31 */
.detail-container {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 12px 14px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.02);
  overflow: hidden;
}
.detail-table-panel {
  flex: 1 1 auto;
  min-height: 0;
  height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.detail-table-host {
  flex: 1 1 auto;
  min-height: 0;
  height: 0;
  width: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}
/*
 * 表体撑满卡片剩余高度。height="100%" 在 flex 子项上经常算成内容高度，
 * 下面会空出一大块，行却被裁掉。桌面改成 flex 列，表体吃掉表头以外的空间。
 * 最后修改：2026-10-14
 */
@media (min-width: 769px) {
  .detail-table-host ::v-deep .el-table {
    flex: 1 1 auto;
    min-height: 0;
    height: 0 !important;
    width: 100%;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }
  .detail-table-host ::v-deep .el-table__header-wrapper {
    flex-shrink: 0;
  }
  .detail-table-host ::v-deep .el-table__body-wrapper {
    flex: 1 1 auto !important;
    min-height: 0;
    height: auto !important;
    max-height: none !important;
    overflow: auto !important;
  }
}
/* 表格定制化重构（无纵线，极简流光交互） */
.modern-table {
  background: transparent !important;
}
.modern-table ::v-deep .el-table__empty-block {
  min-height: 180px;
}
.modern-table ::v-deep th {
  background-color: #f8fafc !important;
  color: #475569 !important;
  font-weight: 700 !important;
  font-size: 13px !important;
  border-bottom: 1.5px solid #e2e8f0 !important;
  padding: 12px 0 !important;
}
.modern-table ::v-deep td {
  padding: 14px 0 !important;
  border-bottom: 1px solid #f1f5f9 !important;
  transition: all 0.25s ease;
}
.modern-table ::v-deep .el-table__row {
  position: relative;
  transition: all 0.25s ease;
}
.modern-table ::v-deep .el-table__row:hover td {
  background-color: rgba(99, 102, 241, 0.02) !important;
}
/* 鼠标滑过整行向右微平移，且左侧浮现高亮条 */
.modern-table ::v-deep .el-table__row:hover td:first-child::before {
  content: "";
  position: absolute;
  left: 0;
  top: 4px;
  bottom: 4px;
  width: 3px;
  background: linear-gradient(180deg, #6366f1, #3b82f6);
  border-radius: 0 4px 4px 0;
  animation: slide-in-bar 0.25s forwards;
}
@keyframes slide-in-bar {
  from { height: 0; opacity: 0; }
  to { height: calc(100% - 8px); opacity: 1; }
}

/* 表格单元格修饰 */
.table-cell-with-icon {
  display: flex;
  align-items: center;
  gap: 8px;
}
.cell-prefix-icon {
  font-size: 15px;
}
.cell-prefix-icon.text-indigo { color: #6366f1; }
.cell-primary-text {
  font-weight: 600;
  color: #1e293b;
}

/* 人员首字彩色头像 */
.table-avatar-cell {
  display: flex;
  align-items: center;
  gap: 10px;
}
.table-avatar {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11.5px;
  font-weight: 700;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
  text-transform: uppercase;
}
.person-name {
  font-weight: 700;
  color: #1e293b;
}

/* 药丸式 Tag 覆盖 */
.modern-table ::v-deep .el-tag {
  border-radius: 12px !important;
  font-weight: 600 !important;
  padding: 0 10px !important;
  border-width: 1px !important;
}
.modern-table ::v-deep .el-tag--success {
  background-color: #ecfdf5 !important;
  border-color: #a7f3d0 !important;
  color: #059669 !important;
}
.modern-table ::v-deep .el-tag--danger {
  background-color: #fef2f2 !important;
  border-color: #fca5a5 !important;
  color: #dc2626 !important;
}
.modern-table ::v-deep .el-tag--info {
  background-color: #f8fafc !important;
  border-color: #e2e8f0 !important;
  color: #64748b !important;
}

/* 状态文字色彩 */
.text-success { color: #10b981 !important; font-weight: 600; }
.text-warning { color: #f59e0b !important; font-weight: 600; }
.text-danger { color: #ef4444 !important; font-weight: 600; }
.text-muted { color: #94a3b8 !important; }

/* 批量与翻页底栏 */
.bottom-actions-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 20px;
  background: rgba(255, 255, 255, 0.6);
  padding: 12px 16px;
  border-radius: 10px;
  border: 1px solid rgba(0, 0, 0, 0.03);
}
.pagination-wrap {
  flex-shrink: 0;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid #f1f5f9;
  background: #fff;
}

/* 弹窗内进度条（同步确认等） */
.bento-progress-wrapper {
  width: 100%;
  height: 4px;
  border-radius: 2px;
  overflow: hidden;
  position: relative;
}
.bento-progress-bar {
  height: 100%;
  border-radius: 2px;
  background: linear-gradient(90deg, #6366f1, #3b82f6);
  transition: width 0.8s cubic-bezier(0.4, 0, 0.2, 1);
}
.notice-banner i {
  font-size: 18px;
}
</style>

<style>
/* 同步说明弹窗：分离信息区块，提升阅读层次 */
.el-dialog.sync-guide-dialog {
  border-radius: 12px !important;
  box-shadow: 0 20px 25px -5px rgba(15, 23, 42, 0.14), 0 10px 10px -5px rgba(15, 23, 42, 0.06) !important;
}
.sync-guide-dialog .el-dialog__header {
  padding: 18px 24px 14px !important;
  border-bottom: 1px solid #eef2f7;
}
.sync-guide-dialog .el-dialog__body {
  padding: 22px 24px 26px !important;
}
.sync-guide-dialog .guide-block {
  padding: 16px 18px 18px;
  border: 1px solid #e8edf5;
  border-radius: 10px;
  background: #fbfcfe;
}
.sync-guide-dialog .guide-block + .guide-block {
  margin-top: 20px;
}
.sync-guide-dialog .desc-badge {
  display: inline-flex;
  align-items: center;
  margin-bottom: 12px;
}
.sync-guide-dialog .guide-steps {
  margin: 0;
  padding-left: 22px;
  line-height: 1.9;
}
.sync-guide-dialog .guide-steps li + li {
  margin-top: 4px;
}
.sync-guide-dialog .guide-text {
  margin: 0;
  line-height: 1.9;
}

/* 统计明细弹窗规范化重构样式 */
.el-dialog.stat-detail-dialog {
  border-radius: 12px !important;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04) !important;
  border: 1px solid rgba(255, 255, 255, 0.8) !important;
  background: rgba(255, 255, 255, 0.98) !important;
}
.stat-detail-dialog .el-dialog__header {
  padding: 18px 20px 12px !important;
  border-bottom: 1px solid #f1f5f9;
}
.stat-detail-dialog .el-dialog__title {
  font-size: 16px !important;
  font-weight: 700 !important;
  color: #1e293b !important;
}
.stat-detail-dialog .el-dialog__body {
  padding: 16px 20px !important;
}
.stat-detail-dialog .el-dialog__footer {
  padding: 12px 20px 15px !important;
  border-top: 1px solid #f1f5f9;
}
.stat-detail-dialog .el-dialog__footer .el-button {
  padding: 8px 16px !important;
  border-radius: 6px !important;
  font-weight: 600 !important;
}
.stat-detail-dialog .el-pagination {
  background: transparent !important;
  box-shadow: none !important;
  padding: 0 !important;
  margin-top: 0 !important;
  margin-bottom: 0 !important;
}

/* 物理审计 Tab：内容区固定高度 */
.el-tabs.physical-audit-tabs {
  border-radius: 8px !important;
  border: 1px solid #e2e8f0 !important;
  box-shadow: none !important;
}
.el-tabs.physical-audit-tabs > .el-tabs__content {
  padding: 12px !important;
}
.el-tabs.physical-audit-tabs .pagination-wrap {
  margin-top: 10px;
  margin-bottom: 0;
}

/* ==========================================================================
   UI UX Pro Max 3.0 确认下发 Dialog 样式重构
   ========================================================================== */
.el-dialog.modern-confirm-dialog {
  border-radius: 16px !important;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.15) !important;
  border: 1px solid rgba(255, 255, 255, 0.8) !important;
  background: rgba(255, 255, 255, 0.98) !important;
  backdrop-filter: blur(20px);
}
.modern-confirm-dialog .el-dialog__header {
  padding: 18px 24px 0 !important;
}
.modern-confirm-dialog .el-dialog__title {
  display: none !important;
}
.modern-confirm-dialog .el-dialog__body {
  padding: 24px !important;
}
.modern-confirm-dialog .el-dialog__footer {
  padding: 12px 24px 24px !important;
  text-align: right;
  border-top: 1px solid rgba(0, 0, 0, 0.05);
}
.modern-confirm-dialog .el-dialog__footer .el-button {
  padding: 10px 20px !important;
  border-radius: 8px !important;
  font-weight: 600 !important;
}
.modern-confirm-dialog .el-dialog__footer .el-button--primary {
  background: #1890ff !important;
  border-color: #1890ff !important;
  box-shadow: 0 4px 10px rgba(24, 144, 255, 0.3) !important;
}

/* 弹窗容器 */
.modern-confirm-dialog .confirm-promax-container {
  display: flex;
  flex-direction: column;
  gap: 16px;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
}

/* 头部样式 */
.modern-confirm-dialog .promax-header {
  display: flex;
  align-items: center;
  gap: 14px;
}
.modern-confirm-dialog .header-icon-box {
  background: rgba(24, 144, 255, 0.1);
  padding: 10px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.modern-confirm-dialog .header-icon-box .rotating-icon {
  font-size: 24px;
  color: #1890ff;
  animation: rotate-sync 3s infinite cubic-bezier(0.4, 0, 0.2, 1);
}
@keyframes rotate-sync {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
.modern-confirm-dialog .header-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.modern-confirm-dialog .header-title {
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  color: #1f1f1f;
}
.modern-confirm-dialog .header-subtitle {
  margin: 0;
  font-size: 12px;
  color: #8c8c8c;
}

/* 区块一：数据源卡片 */
.modern-confirm-dialog .info-section-wrapper {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.modern-confirm-dialog .info-meta-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid #e2e8f0;
  padding-bottom: 10px;
}
.modern-confirm-dialog .meta-item {
  display: flex;
  align-items: center;
  gap: 8px;
}
.modern-confirm-dialog .meta-label {
  font-size: 12px;
  color: #8c8c8c;
}
.modern-confirm-dialog .meta-value {
  font-size: 14px;
  color: #262626;
}
.modern-confirm-dialog .meta-value.font-bold {
  font-weight: 700;
}
.modern-confirm-dialog .node-badge {
  background: #e6f7ff;
  color: #1890ff;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 6px;
  font-size: 12px;
}
.modern-confirm-dialog .grades-box {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.modern-confirm-dialog .grades-label {
  font-size: 12px;
  font-weight: 600;
  color: #8c8c8c;
}
.modern-confirm-dialog .grades-tags-container {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  max-height: 48px;
  overflow-y: hidden;
  padding-right: 4px;
}
/* 自定义细致滚动条 */
.modern-confirm-dialog .grades-tags-container::-webkit-scrollbar,
.modern-confirm-dialog .modern-device-grid::-webkit-scrollbar {
  width: 4px;
}
.modern-confirm-dialog .grades-tags-container::-webkit-scrollbar-thumb,
.modern-confirm-dialog .modern-device-grid::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 2px;
}
.modern-confirm-dialog .grade-confirm-tag {
  font-size: 11px;
  font-weight: 600;
  color: #4f46e5;
  background: #f5f3ff;
  border: 1px solid #ddd6fe;
  padding: 2px 8px;
  border-radius: 6px;
  white-space: nowrap;
}

/* 分割线指示器 */
.modern-confirm-dialog .flow-divider {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  margin: 2px 0;
}
.modern-confirm-dialog .divider-line {
  flex: 1;
  height: 1px;
  background: linear-gradient(to right, rgba(226, 232, 240, 0), rgba(226, 232, 240, 1), rgba(226, 232, 240, 0));
}
.modern-confirm-dialog .divider-badge {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  color: #1890ff;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
}
.modern-confirm-dialog .divider-badge i {
  font-size: 14px;
  font-weight: bold;
}

/* 区块二：接收设备选择卡片 */
.modern-confirm-dialog .device-section-wrapper {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.modern-confirm-dialog .section-title-row {
  display: flex;
  align-items: center;
  gap: 4px;
}
.modern-confirm-dialog .section-title {
  font-size: 13px;
  font-weight: 700;
  color: #595959;
}
.modern-confirm-dialog .section-tip {
  font-size: 11px;
  color: #bfbfbf;
}
.modern-confirm-dialog .modern-device-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
  max-height: 140px;
  overflow-y: auto;
  padding: 2px;
}
.modern-confirm-dialog .modern-device-select-card {
  border: 1.5px solid #e2e8f0;
  background: #ffffff;
  border-radius: 10px;
  padding: 10px 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  user-select: none;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}
.modern-confirm-dialog .modern-device-select-card:hover {
  border-color: #91d5ff;
  box-shadow: 0 4px 12px rgba(24, 144, 255, 0.08);
}
.modern-confirm-dialog .card-left-info {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}
.modern-confirm-dialog .card-camera-icon {
  font-size: 18px;
  color: #8c8c8c;
  background: #f1f5f9;
  padding: 8px;
  border-radius: 8px;
  transition: all 0.25s;
}
.modern-confirm-dialog .card-name-box {
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.modern-confirm-dialog .card-device-title-row {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}
.modern-confirm-dialog .card-device-name {
  font-size: 13px;
  font-weight: 600;
  color: #262626;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.modern-confirm-dialog .card-device-sn {
  font-size: 10px;
  color: #8c8c8c;
  margin-top: 1px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.modern-confirm-dialog .card-status-badge {
  font-size: 18px;
  color: #e2e8f0;
  transition: all 0.25s;
}

/* 在线和离线状态小圆灯 */
.modern-confirm-dialog .status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  display: inline-block;
  flex-shrink: 0;
}
.modern-confirm-dialog .status-dot.online {
  background-color: #52c41a;
  box-shadow: 0 0 6px rgba(82, 196, 26, 0.8);
}
.modern-confirm-dialog .status-dot.offline {
  background-color: #bfbfbf;
}

/* 在线设备选中态 */
.modern-confirm-dialog .modern-device-select-card.device-online.is-selected {
  border-color: #52c41a;
  background: #f6ffed;
  box-shadow: 0 4px 12px rgba(82, 196, 26, 0.12);
}
.modern-confirm-dialog .modern-device-select-card.device-online.is-selected .card-camera-icon {
  color: #52c41a;
  background: rgba(82, 196, 26, 0.1);
}

/* 离线设备未选中态 (默认) */
.modern-confirm-dialog .modern-device-select-card.device-offline {
  border-color: #f1f5f9;
  background: #fafafa;
  opacity: 0.65;
}
.modern-confirm-dialog .modern-device-select-card.device-offline .card-camera-icon {
  color: #bfbfbf;
  background: #f0f0f0;
}

/* 离线设备强行勾选态 */
.modern-confirm-dialog .modern-device-select-card.device-offline.is-selected {
  border-color: #ff4d4f;
  background: #fff2f0;
  opacity: 1;
  box-shadow: 0 4px 12px rgba(255, 77, 79, 0.12);
}
.modern-confirm-dialog .modern-device-select-card.device-offline.is-selected .card-camera-icon {
  color: #ff4d4f;
  background: rgba(255, 77, 79, 0.1);
}

/* 状态图标基础过渡 */
.modern-confirm-dialog .modern-device-select-card .status-check-icon {
  opacity: 0;
  transition: all 0.2s ease;
}
.modern-confirm-dialog .modern-device-select-card.is-selected .status-check-icon {
  opacity: 1;
}
.modern-confirm-dialog .modern-device-select-card.device-online.is-selected .status-check-icon {
  color: #52c41a;
  filter: drop-shadow(0 2px 4px rgba(82, 196, 26, 0.2));
}
.modern-confirm-dialog .modern-device-select-card.device-offline.is-selected .status-check-icon {
  color: #ff4d4f;
  filter: drop-shadow(0 2px 4px rgba(255, 77, 79, 0.2));
}

/* 警告横幅 */
.modern-confirm-dialog .warning-footer-banner {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  background: #fff7e6;
  border: 1px solid #ffd591;
  border-radius: 10px;
  padding: 10px 14px;
  font-size: 11px;
  color: #d46b08;
  line-height: 1.5;
}
.modern-confirm-dialog .warning-footer-banner i {
  font-size: 14px;
  margin-top: 2px;
  flex-shrink: 0;
}

/* 待下发人员人脸冷翠绿标签 */
.modern-confirm-dialog .person-confirm-tag {
  color: #10b981;
  background: #ecfdf5;
  border: 1px solid rgba(16, 185, 129, 0.25);
}
/* 待下发人员人脸无照片红字标签样式 */
.modern-confirm-dialog .person-confirm-tag.no-photo-tag {
  color: #ef4444 !important;
  background: #fef2f2 !important;
  border: 1px dashed rgba(239, 68, 68, 0.4) !important;
}

/* 查看人员明细链接 */
.modern-confirm-dialog .more-persons-link {
  font-size: 11px;
  font-weight: 700;
  color: #1890ff;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  margin-left: 4px;
  text-decoration: underline;
  transition: color 0.2s ease;
  user-select: none;
}
.modern-confirm-dialog .more-persons-link:hover {
  color: #40a9ff;
}

/* ==========================================================================
   UI UX Pro Max 3.0: 摄像头物理人脸库审计精美高级加载动效与步骤指示器样式
   ========================================================================== */
.audit-tabs-container-wrapper {
  position: relative;
}

/* 高级毛玻璃加载层 */
.audit-glass-loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.88);
  backdrop-filter: blur(16px) saturate(120%);
  -webkit-backdrop-filter: blur(16px) saturate(120%);
  z-index: 10;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(226, 232, 240, 0.8);
  animation: auditFadeIn 0.35s cubic-bezier(0.4, 0, 0.2, 1);
}

@keyframes auditFadeIn {
  from { opacity: 0; transform: scale(0.995); }
  to { opacity: 1; transform: scale(1); }
}

.audit-loading-inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 36px;
  width: 100%;
  max-width: 720px;
  padding: 32px;
}

/* 发光雷达与芯片核心 */
.premium-loader-box {
  position: relative;
  width: 100px;
  height: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.radar-glow-ring {
  position: absolute;
  width: 90px;
  height: 90px;
  border: 2px solid rgba(99, 102, 241, 0.12);
  border-radius: 50%;
  box-shadow: 0 0 25px rgba(99, 102, 241, 0.05);
}

.radar-scan-line {
  position: absolute;
  width: 90px;
  height: 90px;
  border-radius: 50%;
  border: 2px solid transparent;
  border-top-color: #6366f1;
  border-right-color: rgba(99, 102, 241, 0.35);
  animation: radar-spin 1.4s infinite linear;
}

@keyframes radar-spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.cpu-core-icon {
  position: relative;
  z-index: 2;
  font-size: 32px;
  color: #6366f1;
  background: #ffffff;
  width: 58px;
  height: 58px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 10px 25px -5px rgba(99, 102, 241, 0.22), 0 8px 10px -6px rgba(99, 102, 241, 0.22);
  border: 1px solid rgba(99, 102, 241, 0.08);
  animation: cpu-pulse 1.8s infinite ease-in-out;
}

@keyframes cpu-pulse {
  0%, 100% { transform: scale(1); box-shadow: 0 10px 25px -5px rgba(99, 102, 241, 0.22); }
  50% { transform: scale(1.08); box-shadow: 0 16px 36px -5px rgba(99, 102, 241, 0.42), 0 0 20px rgba(99, 102, 241, 0.25); }
}

/* 级联拓扑步骤轴 */
.audit-cascade-steps {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 0 8px;
}

.cascade-step {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  width: 90px;
  position: relative;
  transition: all 0.4s ease;
}

.step-icon-circle {
  width: 46px;
  height: 46px;
  border-radius: 50%;
  background: #f8fafc;
  border: 2px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  color: #cbd5e1;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.cascade-step.is-active .step-icon-circle {
  background: rgba(99, 102, 241, 0.08);
  border-color: #6366f1;
  color: #6366f1;
  box-shadow: 0 0 15px rgba(99, 102, 241, 0.25);
  transform: scale(1.12);
}

.cascade-step.is-success .step-icon-circle {
  background: rgba(16, 185, 129, 0.08);
  border-color: #10b981;
  color: #10b981;
  box-shadow: 0 0 12px rgba(16, 185, 129, 0.12);
}

.step-label {
  font-size: 13px;
  font-weight: 600;
  color: #64748b;
  transition: color 0.4s;
}

.cascade-step.is-active .step-label {
  color: #312e81;
  font-weight: 700;
}

.cascade-step.is-success .step-label {
  color: #065f46;
}

.step-status-txt {
  font-size: 10px;
  color: #94a3b8;
  transition: color 0.4s;
}

.cascade-step.is-active .step-status-txt {
  color: #6366f1;
  font-weight: 600;
}

.cascade-step.is-success .step-status-txt {
  color: #10b981;
  font-weight: 600;
}

/* 步骤流向管道线 */
.cascade-line {
  flex: 1;
  height: 4px;
  background: #f1f5f9;
  border-radius: 2px;
  position: relative;
  overflow: hidden;
  margin-top: -24px; /* 向上对齐到圈圈的中心高度 */
  border: 1px solid rgba(226, 232, 240, 0.5);
  transition: all 0.4s;
}

.cascade-line.is-active {
  background: linear-gradient(90deg, #10b981 30%, #f1f5f9 100%);
  border-color: rgba(16, 185, 129, 0.2);
}

.cascade-line.is-success {
  background: #10b981;
  border-color: rgba(16, 185, 129, 0.2);
}

.flow-pulse {
  display: none;
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 45%;
  background: linear-gradient(90deg, rgba(255, 255, 255, 0), rgba(255, 255, 255, 0.9), rgba(255, 255, 255, 0));
  animation: pulse-flow-anim 1.4s infinite linear;
}

.cascade-line.is-active .flow-pulse {
  display: block;
}

@keyframes pulse-flow-anim {
  0% { left: -45%; }
  100% { left: 100%; }
}

/* 微动效 */
.animate-spin-custom {
  animation: mini-rotate 8s infinite linear;
}

.cascade-step.is-active .step-icon-circle i.animate-spin-custom {
  animation: mini-rotate-active 1.8s infinite linear;
}

@keyframes mini-rotate {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

@keyframes mini-rotate-active {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* 状态详细卡片 */
.audit-loading-desc-box {
  background: #f8fafc;
  border: 1px solid rgba(226, 232, 240, 0.8);
  border-radius: 12px;
  padding: 14px 22px;
  display: flex;
  align-items: center;
  gap: 12px;
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.03);
  animation: floatCard 3.5s infinite ease-in-out;
}

@keyframes floatCard {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-4px); }
}

.desc-spinner {
  font-size: 18px;
  color: #6366f1;
}

.desc-text {
  font-size: 13px;
  color: #334155;
  font-weight: 550;
  letter-spacing: 0.2px;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
}
</style>
